import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
import RelatoriosEscolhaModuloButton from '@/modules/reports/presentation/components/RelatoriosEscolhaModuloButton';
import Select from 'react-select';
import { AplicarDecisoesComparecimentoCorregedoria, ComparecimentoDecisaoAcao, } from '@/modules/reports/application/relatorios-corregedoria.service';
import { AutoSaveRelatorio, BuscarDefensorPorCPF, BuscarRelatorioPorId, BuscarTodasLotacoesDefensor, EnviarDiretoCorregedoriaDefensor, ObterComarcaVara, ObterTitularidade, SalvarRelatorio, } from '@/modules/reports/application/relatorios-atividades.service';
import { Container, Card, Form, Button, Row, Col, Badge, ProgressBar, Nav, Table, InputGroup, Overlay, Popover, Spinner } from 'react-bootstrap';
import { ControlesLinha, ProcessoInput, apenasLetras, apenasNumeros, formatarData as formatarDataHelper, } from '../components/RelatorioCivelComponents';
import { Fragment, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { GetPeriodosDoDefensorPorCpf } from '@/modules/business/periodo/periodo.service';
import { ListarOpcoesMovimentacaoTipo } from '@/modules/business/movimentacoes_tipos/movimentacaotipo.service';
import { RELATORIO_CORES } from '../constants/relatorio-cores';
import { ReadDefensorResponseDto, ReadMovimentacaoTipoDto, ReadPeriodoDto, ReadHolidayResponseDto } from '@/api.types';
import { TipoPeriodoDescanso } from '@/modules/business/periodo/enums/tipo-periodo-descanso.enum';
import { diasDaSemana } from '@/shared/utils/datas';
import { getTokenPayload } from '@/modules/reports/application/relatorios-atividades.utils';
import { readHoliday } from '@/modules/business/holidays/holiday.service';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Interface para processo na lista
interface ProcessoItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para autor e natureza da ação
interface AutorNaturezaItem {
  id: string;
  autor: string;
  naturezaAcao: string;
  dataAdicao: Date;
}

// Interface para audiência presencial
interface AudienciaPresencialItem {
  id: string;
  audienciaPresencial: string;
  numeroProtocolo: string;
  data: Date;
}

// Interface para audiência remota
interface AudienciaRemotaItem {
  id: string;
  audienciaRemota: string;
  numeroProcesso: string;
  data: Date;
}

// Interface para atendimento presencial
interface AtendimentoPresencialItem {
  id: string;
  arquivo: File | null;
  arquivoNome?: string;
  arquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  observacao: string;
}

// Interface para atendimento remoto
interface AtendimentoRemotoItem {
  id: string;
  arquivo: File | null;
  arquivoNome?: string;
  arquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  observacao: string;
}

// Interface para sentença/acórdão com fixação de honorários
interface HonorarioSentencaItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para cumprimento de sentença de honorários
interface HonorarioCumprimentoItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para ofício requisitório
interface OficioRequisitorioItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para demais ofícios
interface OficioDemaisItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para designação especial (processos)
interface DesignacaoEspecialProcessoItem {
  id: string;
  tipoPeca: string;
  numeroProcesso: string;
  defensoriaVara: string;
  dataAdicao: Date;
}

// Interface para designação especial (audiências)
interface DesignacaoEspecialAudienciaItem {
  id: string;
  audiencia: string;
  numeroProcesso: string;
  data: Date;
  defensoriaVara: string;
  dataAdicao: Date;
}

// Interface para acordos extrajudiciais
interface AcordoExtrajudicialItem {
  id: string;
  nomeParte: string;
  anexo: File | null;
  anexoNome?: string;
  anexoBase64?: string; // Base64 do arquivo (para salvar no banco)
  dataAdicao: Date;
}

// Interface para opções de natureza da ação
interface NaturezaAcaoOption {
  value: string;
  label: string;
}

// Interface para status de comparecimento por dia
interface DiaComparecimentoStatus {
  dia: number;
  mes?: string; // Mês do status (opcional, padrão é o mês atual)
  ano?: number; // Ano do status (opcional, padrão é o ano atual)
  status: 'presente' | 'ausente' | 'remoto' | 'impedimento' | 'nao_marcado';
  /** Preenchido pela corregedoria ao dar aval: true = remoto validado (conta como presença) */
  remotoValidado?: boolean;
  /** Corregedoria aceitou a justificativa de ausência para este dia */
  ausenteAceitoCorregedoria?: boolean;
  justificativaTexto?: string;
  justificativaArquivo?: File | null;
  justificativaArquivoNome?: string;
  justificativaArquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  tipoImpedimento?: TipoPeriodoDescanso;
  impedimentoDescricao?: string;
}

// Interface para representar um dia no calendário com suas informações de mês/ano
interface DiaCalendario {
  dia: number;
  mes: string;
  ano: number;
  isMesAtual: boolean; // Indica se o dia pertence ao mês atual sendo visualizado
}

// Função para obter o nome do tipo de período
const getTipoPeriodoNome = (tipo: TipoPeriodoDescanso): string => {
  const nomes: Record<TipoPeriodoDescanso, string> = {
    [TipoPeriodoDescanso.Ferias]: 'Férias',
    [TipoPeriodoDescanso.FolgaNatalicia]: 'Folga Natalícia',
    [TipoPeriodoDescanso.AbonoTrabalhado]: 'Abono Trabalhado',
    [TipoPeriodoDescanso.AbonoEspecial]: 'Abono Especial',
    [TipoPeriodoDescanso.LicencaPremio]: 'Licença Prêmio',
    [TipoPeriodoDescanso.LicencaOutros]: 'Licença Outros',
    [TipoPeriodoDescanso.Afastamento]: 'Afastamento',
  };
  return nomes[tipo] || 'Impedimento';
};

// Lista de tipos de audiências (presenciais e remotas)
const TIPOS_AUDIENCIAS = [
  'Audiência de Instrução',
  'Audiência de Conciliação',
  'Audiência de Justificação',
  'Audiência de Oitiva',
  'Audiência de Interrogatório',
  'Audiência de Pronúncia',
  'Audiência de Sentença',
  'Audiência de Instrução e Julgamento',
  'Audiência de Conciliação e Instrução',
  'Audiência Virtual',
  'Audiência Remota',
  'Audiência por Videoconferência',
  'Outra',
];

// Definição das etapas do wizard
const WIZARD_STEPS = [
  { id: 1, title: 'Comparecimento ao Fórum', shortTitle: 'Comparecimento' },
  { id: 2, title: 'Autor e Natureza da Ação', shortTitle: 'Autor/Natureza' },
  { id: 3, title: 'Contestação', shortTitle: 'Contestação' },
  { id: 4, title: 'Reconvenção', shortTitle: 'Reconvenção' },
  { id: 5, title: 'Réplica', shortTitle: 'Réplica' },
  { id: 6, title: 'Manifestação nos Autos', shortTitle: 'Manifestação' },
  { id: 7, title: 'Razões Finais', shortTitle: 'Razões Finais' },
  { id: 8, title: 'Audiências', shortTitle: 'Audiências' },
  { id: 9, title: 'Recursos', shortTitle: 'Recursos' },
  { id: 10, title: 'Atendimentos', shortTitle: 'Atendimentos' },
  { id: 11, title: 'Acordos Extrajudiciais', shortTitle: 'Acordos' },
  { id: 12, title: 'Honorários', shortTitle: 'Honorários' },
  { id: 13, title: 'Ofícios Expedidos', shortTitle: 'Ofícios' },
  { id: 14, title: 'Designação Especial (Processos)', shortTitle: 'Desig. Processos' },
  { id: 15, title: 'Designação Especial (Audiências)', shortTitle: 'Desig. Audiências' },
  { id: 16, title: 'Outros Procedimentos', shortTitle: 'Outros' },
];

// Interface para os dados do formulário de relatório cível
interface RelatorioCivelFormData {
  // Informações básicas
  defensoriaId: number;
  defensoriaName: string;
  mes: string;
  ano: number;

  // Etapa 1 - Comparecimento ao Fórum
  comparecimento: {
    diasComparecimento: number;
    horasTrabalho: number;
    audienciasRealizadas: number;
    audienciasConciliacao: number;
    audienciasInstrucao: number;
    despachos: number;
    diasStatus: DiaComparecimentoStatus[]; // Array com status de cada dia do mês
  };

  // Etapa 2 - Autor e Natureza da Ação
  autorNatureza: {
    itens: AutorNaturezaItem[];
  };

  // Etapa 3 - Contestação (lista de processos)
  contestacao: {
    processos: ProcessoItem[];
  };

  // Etapa 4 - Reconvenção (lista de processos)
  reconvencao: {
    processos: ProcessoItem[];
  };

  // Etapa 5 - Réplica (lista de processos)
  replica: {
    processos: ProcessoItem[];
  };

  // Etapa 6 - Manifestação nos Autos (lista de processos)
  manifestacao: {
    processos: ProcessoItem[];
  };

  // Etapa 7 - Razões Finais (lista de processos)
  razoesFinais: {
    processos: ProcessoItem[];
  };

  // Etapa 7 - Audiências (presenciais e remotas)
  audiencias: {
    presenciais: AudienciaPresencialItem[];
    remotas: AudienciaRemotaItem[];
  };

  // Etapa 8 - Recursos (lista de processos)
  recursos: {
    processos: ProcessoItem[];
  };

  // Etapa 9 - Atendimento (presenciais e remotos)
  atendimento: {
    presenciais: AtendimentoPresencialItem[];
    remotas: AtendimentoRemotoItem[];
  };

  // Etapa 10 - Acordos Extrajudiciais
  acordosExtrajudiciais: {
    itens: AcordoExtrajudicialItem[];
  };

  // Etapa 11 - Honorários (sentença/acórdão e cumprimento)
  honorarios: {
    sentencaAcordao: HonorarioSentencaItem[];
    cumprimento: HonorarioCumprimentoItem[];
  };

  // Etapa 12 - Ofícios Expedidos (requisitórios e demais)
  oficiosExpedidos: {
    requisitorios: OficioRequisitorioItem[];
    demais: OficioDemaisItem[];
  };

  // Etapa 13 - Designação Especial (Processos)
  designacaoEspecialProcessos: {
    itens: DesignacaoEspecialProcessoItem[];
  };

  // Etapa 14 - Designação Especial (Audiências)
  designacaoEspecialAudiencias: {
    itens: DesignacaoEspecialAudienciaItem[];
  };

  // Etapa 15 - Outros Procedimentos (lista de processos)
  outrosProcedimentos: {
    processos: ProcessoItem[];
  };
}

// Valores iniciais do formulário
const initialFormData: RelatorioCivelFormData = {
  defensoriaId: 0,
  defensoriaName: '',
  mes: '',
  ano: 0,
  comparecimento: {
    diasComparecimento: 0,
    horasTrabalho: 0,
    audienciasRealizadas: 0,
    audienciasConciliacao: 0,
    audienciasInstrucao: 0,
    despachos: 0,
    diasStatus: [],
  },
  autorNatureza: {
    itens: [],
  },
  contestacao: {
    processos: [],
  },
  reconvencao: {
    processos: [],
  },
  replica: {
    processos: [],
  },
  manifestacao: {
    processos: [],
  },
  razoesFinais: {
    processos: [],
  },
  audiencias: {
    presenciais: [],
    remotas: [],
  },
  recursos: {
    processos: [],
  },
  atendimento: {
    presenciais: [],
    remotas: [],
  },
  acordosExtrajudiciais: {
    itens: [],
  },
  honorarios: {
    sentencaAcordao: [],
    cumprimento: [],
  },
  oficiosExpedidos: {
    requisitorios: [],
    demais: [],
  },
  designacaoEspecialProcessos: {
    itens: [],
  },
  designacaoEspecialAudiencias: {
    itens: [],
  },
  outrosProcedimentos: {
    processos: [],
  },
};

export default function RelatorioCivelPage() {
  const context = useContext(MyContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (!context) {
    throw new Error('RelatorioCivelPage deve ser usado dentro de um MyProvider');
  }

  const { state } = context;

  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isViewMode] = useState(
    searchParams.get('view') === 'true'
  );
  const origemVisualizacao = searchParams.get('origem') || '';
  const [relatorioId, setRelatorioId] = useState<number | null>(
    searchParams.get('id') ? parseInt(searchParams.get('id')!) : null
  );
  /** Corregedoria devolveu ao defensor: envio final é direto à Corregedoria. */
  const [metaReenvioCorregedoria, setMetaReenvioCorregedoria] = useState(false);
  /** Metadados do relatório em modo visualização (Corregedoria decide comparecimento antes do aval final). */
  const [relatorioMetaCorregedoria, setRelatorioMetaCorregedoria] = useState<{
    status?: string;
    corregedoriaAprovadoEm?: string;
  } | null>(null);
  const [corregedoriaDecisaoDiaLoading, setCorregedoriaDecisaoDiaLoading] = useState<string | null>(null);
  const [defensorData, setDefensorData] = useState<ReadDefensorResponseDto | null>(null);
  const [vara, setVara] = useState('');
  const [titularidade, setTitularidade] = useState('nao-definido');
  const [naturezaAcaoOptions, setNaturezaAcaoOptions] = useState<NaturezaAcaoOption[]>([
    { value: '', label: 'Selecione...' }
  ]);
  const [periodosDefensor, setPeriodosDefensor] = useState<ReadPeriodoDto[]>([]);
  const [feriados, setFeriados] = useState<ReadHolidayResponseDto[]>([]);
  const [mostrarResumoRelatorio, setMostrarResumoRelatorio] = useState(false);

  // Dados do formulário
  const [formData, setFormData] = useState<RelatorioCivelFormData>({
    ...initialFormData,
    defensoriaId: parseInt(searchParams.get('defensoriaId') || '0'),
    defensoriaName: searchParams.get('defensoriaName') || '',
    mes: String(searchParams.get('mes') || String(new Date().getMonth() + 1).padStart(2, '0')).padStart(2, '0'),
    ano: parseInt(searchParams.get('ano') || String(new Date().getFullYear())),
  });

  // Carregar feriados (bloqueios) que afetam a defensoria, para usar no calendário de comparecimento
  useEffect(() => {
    if (!formData.defensoriaId) {
      setFeriados([]);
      return;
    }

    readHoliday({
      skip: 0,
      take: 1000,
      defensoriaId: formData.defensoriaId,
    })
      .then((result) => {
        setFeriados(result[0] || []);
      })
      .catch((error) => {
        console.error('Erro ao carregar feriados para o calendário de comparecimento:', error);
      });
  }, [formData.defensoriaId]);

  // Estados para inputs de número de processo em cada etapa
  const [novoProcessoContestacao, setNovoProcessoContestacao] = useState('');
  const [novoProcessoReconvencao, setNovoProcessoReconvencao] = useState('');
  const [novoProcessoReplica, setNovoProcessoReplica] = useState('');
  const [novoProcessoManifestacao, setNovoProcessoManifestacao] = useState('');
  const [novoProcessoRazoesFinais, setNovoProcessoRazoesFinais] = useState('');
  // Estados para Audiências Presenciais
  const [novaAudienciaPresencial, setNovaAudienciaPresencial] = useState('');
  const [novoNumeroProtocolo, setNovoNumeroProtocolo] = useState('');
  const [novaDataPresencial, setNovaDataPresencial] = useState('');

  // Estados para Audiências Remotas
  const [novaAudienciaRemota, setNovaAudienciaRemota] = useState('');
  const [novoNumeroProcessoRemota, setNovoNumeroProcessoRemota] = useState('');
  const [novaDataRemota, setNovaDataRemota] = useState('');

  // Estados para edição de Audiências Presenciais
  const [editandoAudienciaPresencial, setEditandoAudienciaPresencial] = useState<string | null>(null);
  const [valorEdicaoAudienciaPresencial, setValorEdicaoAudienciaPresencial] = useState('');
  const [valorEdicaoNumeroProtocolo, setValorEdicaoNumeroProtocolo] = useState('');
  const [valorEdicaoDataPresencial, setValorEdicaoDataPresencial] = useState('');

  // Estados para edição de Audiências Remotas
  const [editandoAudienciaRemota, setEditandoAudienciaRemota] = useState<string | null>(null);
  const [valorEdicaoAudienciaRemota, setValorEdicaoAudienciaRemota] = useState('');
  const [valorEdicaoNumeroProcessoRemota, setValorEdicaoNumeroProcessoRemota] = useState('');
  const [valorEdicaoDataRemota, setValorEdicaoDataRemota] = useState('');

  // Estados para edição de Atendimentos Presenciais
  const [editandoAtendimentoPresencial, setEditandoAtendimentoPresencial] = useState<string | null>(null);
  const [valorEdicaoArquivoAtendimentoPresencial, setValorEdicaoArquivoAtendimentoPresencial] = useState<File | null>(null);
  const [valorEdicaoObservacaoAtendimentoPresencial, setValorEdicaoObservacaoAtendimentoPresencial] = useState('');
  const arquivoAtendimentoPresencialEdicaoInputRef = useRef<HTMLInputElement>(null);

  // Estados para edição de Atendimentos Remotos
  const [editandoAtendimentoRemoto, setEditandoAtendimentoRemoto] = useState<string | null>(null);
  const [valorEdicaoArquivoAtendimentoRemoto, setValorEdicaoArquivoAtendimentoRemoto] = useState<File | null>(null);
  const [valorEdicaoObservacaoAtendimentoRemoto, setValorEdicaoObservacaoAtendimentoRemoto] = useState('');
  const arquivoAtendimentoRemotoEdicaoInputRef = useRef<HTMLInputElement>(null);

  const [novoProcessoRecursos, setNovoProcessoRecursos] = useState('');
  // Estados para Atendimentos Presenciais
  const [novoArquivoAtendimentoPresencial, setNovoArquivoAtendimentoPresencial] = useState<File | null>(null);
  const [novaObservacaoAtendimentoPresencial, setNovaObservacaoAtendimentoPresencial] = useState('');
  const arquivoAtendimentoPresencialInputRef = useRef<HTMLInputElement>(null);

  // Estados para Atendimentos Remotos
  const [novoArquivoAtendimentoRemoto, setNovoArquivoAtendimentoRemoto] = useState<File | null>(null);
  const [novaObservacaoAtendimentoRemoto, setNovaObservacaoAtendimentoRemoto] = useState('');
  const arquivoAtendimentoRemotoInputRef = useRef<HTMLInputElement>(null);
  // Estados para Acordos Extrajudiciais
  const [novoNomeParteAcordo, setNovoNomeParteAcordo] = useState('');
  const [novoAnexoAcordo, setNovoAnexoAcordo] = useState<File | null>(null);

  // Estados para Honorários - Sentença/Acórdão
  const [novoProcessoSentencaAcordao, setNovoProcessoSentencaAcordao] = useState('');

  // Estados para Honorários - Cumprimento
  const [novoProcessoCumprimento, setNovoProcessoCumprimento] = useState('');
  // Estados para Ofícios Expedidos - Requisitórios
  const [novoProcessoRequisitorio, setNovoProcessoRequisitorio] = useState('');

  // Estados para Ofícios Expedidos - Demais Ofícios
  const [novoProcessoDemaisOficios, setNovoProcessoDemaisOficios] = useState('');
  // Estados para Designação Especial (Processos)
  const [novoTipoPeca, setNovoTipoPeca] = useState('');
  const [novoNumeroProcessoDesig, setNovoNumeroProcessoDesig] = useState('');
  const [novaDefensoriaVara, setNovaDefensoriaVara] = useState('');

  // Estados para Designação Especial (Audiências)
  const [novaAudienciaDesig, setNovaAudienciaDesig] = useState('');
  const [novoNumeroProcessoDesigAud, setNovoNumeroProcessoDesigAud] = useState('');
  const [novaDataDesigAud, setNovaDataDesigAud] = useState('');
  const [novaDefensoriaVaraDesigAud, setNovaDefensoriaVaraDesigAud] = useState('');

  // Estados para edição de Acordos Extrajudiciais
  const [editandoAcordoExtrajudicial, setEditandoAcordoExtrajudicial] = useState<string | null>(null);
  const [valorEdicaoNomeParteAcordo, setValorEdicaoNomeParteAcordo] = useState('');
  const [valorEdicaoAnexoAcordo, setValorEdicaoAnexoAcordo] = useState<File | null>(null);
  const anexoAcordoEdicaoInputRef = useRef<HTMLInputElement>(null);

  // Estados para edição de Honorários - Sentença/Acórdão
  const [editandoHonorarioSentencaAcordao, setEditandoHonorarioSentencaAcordao] = useState<string | null>(null);
  const [valorEdicaoProcessoSentencaAcordao, setValorEdicaoProcessoSentencaAcordao] = useState('');

  // Estados para edição de Honorários - Cumprimento
  const [editandoHonorarioCumprimento, setEditandoHonorarioCumprimento] = useState<string | null>(null);
  const [valorEdicaoProcessoCumprimento, setValorEdicaoProcessoCumprimento] = useState('');

  // Estados para edição de Ofícios Expedidos - Requisitórios
  const [editandoOficioRequisitorio, setEditandoOficioRequisitorio] = useState<string | null>(null);
  const [valorEdicaoProcessoRequisitorio, setValorEdicaoProcessoRequisitorio] = useState('');

  // Estados para edição de Ofícios Expedidos - Demais Ofícios
  const [editandoOficioDemais, setEditandoOficioDemais] = useState<string | null>(null);
  const [valorEdicaoProcessoDemaisOficios, setValorEdicaoProcessoDemaisOficios] = useState('');

  // Estados para edição de Designação Especial (Processos)
  const [editandoDesignacaoEspecialProcesso, setEditandoDesignacaoEspecialProcesso] = useState<string | null>(null);
  const [valorEdicaoTipoPeca, setValorEdicaoTipoPeca] = useState('');
  const [valorEdicaoNumeroProcessoDesig, setValorEdicaoNumeroProcessoDesig] = useState('');
  const [valorEdicaoDefensoriaVara, setValorEdicaoDefensoriaVara] = useState('');

  // Estados para edição de Designação Especial (Audiências)
  const [editandoDesignacaoEspecialAudiencia, setEditandoDesignacaoEspecialAudiencia] = useState<string | null>(null);
  const [valorEdicaoAudienciaDesig, setValorEdicaoAudienciaDesig] = useState('');
  const [valorEdicaoNumeroProcessoDesigAud, setValorEdicaoNumeroProcessoDesigAud] = useState('');
  const [valorEdicaoDataDesigAud, setValorEdicaoDataDesigAud] = useState('');
  const [valorEdicaoDefensoriaVaraDesigAud, setValorEdicaoDefensoriaVaraDesigAud] = useState('');

  const [novoProcessoOutros, setNovoProcessoOutros] = useState('');

  // Tipo para todas as seções de processos
  type ProcessoSection = 'contestacao' | 'reconvencao' | 'replica' | 'manifestacao' | 'razoesFinais' |
    'recursos' |
    'outrosProcedimentos';

  // Funções para adicionar/remover processos
  const adicionarProcesso = (
    section: ProcessoSection,
    numeroProcesso: string,
    setNovoProcesso: (value: string) => void
  ) => {
    if (!numeroProcesso.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    // Verifica se já existe
    const secao = formData[section] as { processos: ProcessoItem[] };
    if (secao.processos.some((p: ProcessoItem) => p.numeroProcesso === numeroProcesso.trim())) {
      toast.warning('Este processo já foi adicionado.');
      return;
    }

    const novoItem: ProcessoItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: numeroProcesso.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as { processos: ProcessoItem[] }),
        processos: [...(prev[section] as { processos: ProcessoItem[] }).processos, novoItem],
      },
    }));

    setNovoProcesso('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerProcesso = (
    section: ProcessoSection,
    id: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as { processos: ProcessoItem[] }),
        processos: (prev[section] as { processos: ProcessoItem[] }).processos.filter((p: ProcessoItem) => p.id !== id),
      },
    }));
    toast.info('Processo removido.');
  };

  // Estados para edição de processos
  const [editandoProcesso, setEditandoProcesso] = useState<{ section: string; id: string } | null>(null);
  const [valorEdicao, setValorEdicao] = useState('');

  // Estados para confirmação de exclusão
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<{ section: string; id: string } | null>(null);
  const excluirButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Estados para Etapa 2 - Autor e Natureza da Ação
  const [novoAutor, setNovoAutor] = useState('');
  const [novaNatureza, setNovaNatureza] = useState('');
  const [editandoAutorNatureza, setEditandoAutorNatureza] = useState<string | null>(null);
  const [valorEdicaoAutor, setValorEdicaoAutor] = useState('');
  const [valorEdicaoNatureza, setValorEdicaoNatureza] = useState('');
  const [confirmandoExclusaoAutor, setConfirmandoExclusaoAutor] = useState<string | null>(null);
  const excluirAutorButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [quantidadeLinhasAutor, setQuantidadeLinhasAutor] = useState(1);

  // Estados para adicionar múltiplas linhas
  const [quantidadeLinhas, setQuantidadeLinhas] = useState<{ [key: string]: number }>({
    contestacao: 1,
    reconvencao: 1,
    replica: 1,
    manifestacao: 1,
    razoesFinais: 1,
    recursos: 1,
    acordosExtrajudiciais: 1,
    designacaoEspecialProcessos: 1,
    designacaoEspecialAudiencias: 1,
    outrosProcedimentos: 1,
    // Sub-etapas
    audienciasPresenciais: 1,
    audienciasRemotas: 1,
    atendimentosPresenciais: 1,
    atendimentosRemotos: 1,
    honorariosSentencaAcordao: 1,
    honorariosCumprimento: 1,
    oficiosRequisitorios: 1,
    oficiosDemais: 1,
  });

  // Estados para o calendário de comparecimento
  const [justificativaModalDia, setJustificativaModalDia] = useState<number | null>(null);
  const [justificativaTextoTemp, setJustificativaTextoTemp] = useState('');
  const [justificativaArquivoTemp, setJustificativaArquivoTemp] = useState<File | null>(null);
  const justificativaButtonRefs = useRef<{ [key: number]: HTMLElement | null }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const anexoAcordoInputRef = useRef<HTMLInputElement>(null);

  // Funções para edição de processos
  const iniciarEdicao = (
    section: ProcessoSection,
    processo: ProcessoItem
  ) => {
    setEditandoProcesso({ section, id: processo.id });
    setValorEdicao(processo.numeroProcesso);
  };

  const cancelarEdicao = () => {
    setEditandoProcesso(null);
    setValorEdicao('');
  };

  const salvarEdicao = (
    section: ProcessoSection,
    id: string
  ) => {
    if (!valorEdicao.trim()) {
      toast.warning('O número do processo não pode estar vazio.');
      return;
    }

    // Verifica se já existe outro processo com o mesmo número
    const secao = formData[section] as { processos: ProcessoItem[] };
    if (secao.processos.some((p: ProcessoItem) => p.numeroProcesso === valorEdicao.trim() && p.id !== id)) {
      toast.warning('Já existe um processo com este número.');
      return;
    }

    setFormData(prev => {
      const secaoAnterior = prev[section] as { processos: ProcessoItem[] };
      return {
        ...prev,
        [section]: {
          ...secaoAnterior,
          processos: secaoAnterior.processos.map((p: ProcessoItem) =>
            p.id === id ? { ...p, numeroProcesso: valorEdicao.trim() } : p
          ),
        },
      };
    });

    setEditandoProcesso(null);
    setValorEdicao('');
    toast.success('Processo atualizado com sucesso!');
  };

  // Funções para Etapa 2 - Autor e Natureza da Ação
  const adicionarAutorNatureza = () => {
    if (!novoAutor.trim()) {
      toast.warning('Informe o nome do autor.');
      return;
    }
    if (!novaNatureza) {
      toast.warning('Selecione a natureza da ação.');
      return;
    }

    const novoItem: AutorNaturezaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      autor: novoAutor.trim(),
      naturezaAcao: novaNatureza,
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      autorNatureza: {
        itens: [...prev.autorNatureza.itens, novoItem],
      },
    }));

    setNovoAutor('');
    setNovaNatureza('');
    toast.success('Autor adicionado com sucesso!');
  };

  const removerAutorNatureza = (id: string) => {
    setFormData(prev => ({
      ...prev,
      autorNatureza: {
        itens: prev.autorNatureza.itens.filter(item => item.id !== id),
      },
    }));
    toast.info('Autor removido.');
  };

  const iniciarEdicaoAutorNatureza = (item: AutorNaturezaItem) => {
    setEditandoAutorNatureza(item.id);
    setValorEdicaoAutor(item.autor);
    setValorEdicaoNatureza(item.naturezaAcao);
  };

  const cancelarEdicaoAutorNatureza = () => {
    setEditandoAutorNatureza(null);
    setValorEdicaoAutor('');
    setValorEdicaoNatureza('');
  };

  const salvarEdicaoAutorNatureza = (id: string) => {
    if (!valorEdicaoAutor.trim()) {
      toast.warning('O nome do autor não pode estar vazio.');
      return;
    }
    if (!valorEdicaoNatureza) {
      toast.warning('Selecione a natureza da ação.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      autorNatureza: {
        itens: prev.autorNatureza.itens.map(item =>
          item.id === id
            ? { ...item, autor: valorEdicaoAutor.trim(), naturezaAcao: valorEdicaoNatureza }
            : item
        ),
      },
    }));

    setEditandoAutorNatureza(null);
    setValorEdicaoAutor('');
    setValorEdicaoNatureza('');
    toast.success('Autor atualizado com sucesso!');
  };

  // Funções para Etapa 7 - Audiências Presenciais
  const adicionarAudienciaPresencial = () => {
    if (!novaAudienciaPresencial.trim()) {
      toast.warning('Selecione o tipo de audiência presencial.');
      return;
    }
    if (!novoNumeroProtocolo.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDataPresencial) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    const novoItem: AudienciaPresencialItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audienciaPresencial: novaAudienciaPresencial.trim(),
      numeroProtocolo: novoNumeroProtocolo.trim(),
      data: new Date(novaDataPresencial),
    };

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: [...prev.audiencias.presenciais, novoItem],
      },
    }));

    setNovaAudienciaPresencial('');
    setNovoNumeroProtocolo('');
    setNovaDataPresencial('');
    toast.success('Audiência presencial adicionada com sucesso!');
  };

  const removerAudienciaPresencial = (id: string) => {
    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: prev.audiencias.presenciais.filter(item => item.id !== id),
      },
    }));
    toast.success('Audiência presencial removida com sucesso!');
  };

  const adicionarMultiplasLinhasAudienciasPresenciais = () => {
    const quantidade = quantidadeLinhas.audienciasPresenciais;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AudienciaPresencialItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        audienciaPresencial: '',
        numeroProtocolo: '',
        data: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: [...prev.audiencias.presenciais, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 7 - Audiências Remotas
  const adicionarAudienciaRemota = () => {
    if (!novaAudienciaRemota.trim()) {
      toast.warning('Selecione o tipo de audiência remota.');
      return;
    }
    if (!novoNumeroProcessoRemota.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDataRemota) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    const novoItem: AudienciaRemotaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audienciaRemota: novaAudienciaRemota.trim(),
      numeroProcesso: novoNumeroProcessoRemota.trim(),
      data: new Date(novaDataRemota),
    };

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: [...prev.audiencias.remotas, novoItem],
      },
    }));

    setNovaAudienciaRemota('');
    setNovoNumeroProcessoRemota('');
    setNovaDataRemota('');
    toast.success('Audiência remota adicionada com sucesso!');
  };

  const removerAudienciaRemota = (id: string) => {
    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: prev.audiencias.remotas.filter(item => item.id !== id),
      },
    }));
    toast.success('Audiência remota removida com sucesso!');
  };

  const adicionarMultiplasLinhasAudienciasRemotas = () => {
    const quantidade = quantidadeLinhas.audienciasRemotas;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AudienciaRemotaItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        audienciaRemota: '',
        numeroProcesso: '',
        data: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: [...prev.audiencias.remotas, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para edição de Audiências Presenciais
  const iniciarEdicaoAudienciaPresencial = (item: AudienciaPresencialItem) => {
    setEditandoAudienciaPresencial(item.id);
    setValorEdicaoAudienciaPresencial(item.audienciaPresencial);
    setValorEdicaoNumeroProtocolo(item.numeroProtocolo);
    setValorEdicaoDataPresencial(new Date(item.data).toISOString().split('T')[0]);
  };

  const cancelarEdicaoAudienciaPresencial = () => {
    setEditandoAudienciaPresencial(null);
    setValorEdicaoAudienciaPresencial('');
    setValorEdicaoNumeroProtocolo('');
    setValorEdicaoDataPresencial('');
  };

  const salvarEdicaoAudienciaPresencial = (id: string) => {
    if (!valorEdicaoAudienciaPresencial.trim()) {
      toast.warning('Selecione o tipo de audiência presencial.');
      return;
    }
    if (!valorEdicaoNumeroProtocolo.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDataPresencial) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: prev.audiencias.presenciais.map(item =>
          item.id === id
            ? {
              ...item,
              audienciaPresencial: valorEdicaoAudienciaPresencial.trim(),
              numeroProtocolo: valorEdicaoNumeroProtocolo.trim(),
              data: new Date(valorEdicaoDataPresencial),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoAudienciaPresencial();
    toast.success('Audiência presencial atualizada com sucesso!');
  };

  // Funções para edição de Audiências Remotas
  const iniciarEdicaoAudienciaRemota = (item: AudienciaRemotaItem) => {
    setEditandoAudienciaRemota(item.id);
    setValorEdicaoAudienciaRemota(item.audienciaRemota);
    setValorEdicaoNumeroProcessoRemota(item.numeroProcesso);
    setValorEdicaoDataRemota(new Date(item.data).toISOString().split('T')[0]);
  };

  const cancelarEdicaoAudienciaRemota = () => {
    setEditandoAudienciaRemota(null);
    setValorEdicaoAudienciaRemota('');
    setValorEdicaoNumeroProcessoRemota('');
    setValorEdicaoDataRemota('');
  };

  const salvarEdicaoAudienciaRemota = (id: string) => {
    if (!valorEdicaoAudienciaRemota.trim()) {
      toast.warning('Selecione o tipo de audiência remota.');
      return;
    }
    if (!valorEdicaoNumeroProcessoRemota.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDataRemota) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: prev.audiencias.remotas.map(item =>
          item.id === id
            ? {
              ...item,
              audienciaRemota: valorEdicaoAudienciaRemota.trim(),
              numeroProcesso: valorEdicaoNumeroProcessoRemota.trim(),
              data: new Date(valorEdicaoDataRemota),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoAudienciaRemota();
    toast.success('Audiência remota atualizada com sucesso!');
  };

  // Funções para Etapa 9 - Atendimentos Presenciais
  const adicionarAtendimentoPresencial = () => {
    if (!novoArquivoAtendimentoPresencial) {
      toast.warning('Selecione um arquivo.');
      return;
    }

    const novoItem: AtendimentoPresencialItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      arquivo: novoArquivoAtendimentoPresencial,
      arquivoNome: novoArquivoAtendimentoPresencial.name,
      observacao: novaObservacaoAtendimentoPresencial.trim(),
    };

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        presenciais: [...prev.atendimento.presenciais, novoItem],
      },
    }));

    setNovoArquivoAtendimentoPresencial(null);
    setNovaObservacaoAtendimentoPresencial('');
    if (arquivoAtendimentoPresencialInputRef.current) {
      arquivoAtendimentoPresencialInputRef.current.value = '';
    }
    toast.success('Atendimento presencial adicionado com sucesso!');
  };

  const handleFileChangeAtendimentoPresencial = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setNovoArquivoAtendimentoPresencial(file);
    }
  };

  const removerAtendimentoPresencial = (id: string) => {
    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        presenciais: prev.atendimento.presenciais.filter(item => item.id !== id),
      },
    }));
    toast.success('Atendimento presencial removido com sucesso!');
  };

  const adicionarMultiplasLinhasAtendimentosPresenciais = () => {
    const quantidade = quantidadeLinhas.atendimentosPresenciais;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AtendimentoPresencialItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        arquivo: null,
        observacao: '',
      });
    }

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        presenciais: [...prev.atendimento.presenciais, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 9 - Atendimentos Remotos
  const adicionarAtendimentoRemoto = () => {
    if (!novoArquivoAtendimentoRemoto) {
      toast.warning('Selecione um arquivo.');
      return;
    }

    const novoItem: AtendimentoRemotoItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      arquivo: novoArquivoAtendimentoRemoto,
      arquivoNome: novoArquivoAtendimentoRemoto.name,
      observacao: novaObservacaoAtendimentoRemoto.trim(),
    };

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        remotas: [...prev.atendimento.remotas, novoItem],
      },
    }));

    setNovoArquivoAtendimentoRemoto(null);
    setNovaObservacaoAtendimentoRemoto('');
    if (arquivoAtendimentoRemotoInputRef.current) {
      arquivoAtendimentoRemotoInputRef.current.value = '';
    }
    toast.success('Atendimento remoto adicionado com sucesso!');
  };

  const handleFileChangeAtendimentoRemoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setNovoArquivoAtendimentoRemoto(file);
    }
  };

  const removerAtendimentoRemoto = (id: string) => {
    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        remotas: prev.atendimento.remotas.filter(item => item.id !== id),
      },
    }));
    toast.success('Atendimento remoto removido com sucesso!');
  };

  const adicionarMultiplasLinhasAtendimentosRemotos = () => {
    const quantidade = quantidadeLinhas.atendimentosRemotos;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AtendimentoRemotoItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        arquivo: null,
        observacao: '',
      });
    }

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        remotas: [...prev.atendimento.remotas, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para edição de Atendimentos Presenciais
  const iniciarEdicaoAtendimentoPresencial = (item: AtendimentoPresencialItem) => {
    setEditandoAtendimentoPresencial(item.id);
    setValorEdicaoArquivoAtendimentoPresencial(item.arquivo);
    setValorEdicaoObservacaoAtendimentoPresencial(item.observacao);
  };

  const cancelarEdicaoAtendimentoPresencial = () => {
    setEditandoAtendimentoPresencial(null);
    setValorEdicaoArquivoAtendimentoPresencial(null);
    setValorEdicaoObservacaoAtendimentoPresencial('');
    if (arquivoAtendimentoPresencialEdicaoInputRef.current) {
      arquivoAtendimentoPresencialEdicaoInputRef.current.value = '';
    }
  };

  const salvarEdicaoAtendimentoPresencial = (id: string) => {
    if (!valorEdicaoArquivoAtendimentoPresencial && !formData.atendimento.presenciais.find(item => item.id === id)?.arquivo) {
      toast.warning('Selecione um arquivo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        presenciais: prev.atendimento.presenciais.map(item =>
          item.id === id
            ? {
              ...item,
              arquivo: valorEdicaoArquivoAtendimentoPresencial || item.arquivo,
              arquivoNome: valorEdicaoArquivoAtendimentoPresencial ? valorEdicaoArquivoAtendimentoPresencial.name : item.arquivoNome,
              observacao: valorEdicaoObservacaoAtendimentoPresencial.trim(),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoAtendimentoPresencial();
    toast.success('Atendimento presencial atualizado com sucesso!');
  };

  const handleFileChangeAtendimentoPresencialEdicao = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setValorEdicaoArquivoAtendimentoPresencial(file);
    }
  };

  // Funções para edição de Atendimentos Remotos
  const iniciarEdicaoAtendimentoRemoto = (item: AtendimentoRemotoItem) => {
    setEditandoAtendimentoRemoto(item.id);
    setValorEdicaoArquivoAtendimentoRemoto(item.arquivo);
    setValorEdicaoObservacaoAtendimentoRemoto(item.observacao);
  };

  const cancelarEdicaoAtendimentoRemoto = () => {
    setEditandoAtendimentoRemoto(null);
    setValorEdicaoArquivoAtendimentoRemoto(null);
    setValorEdicaoObservacaoAtendimentoRemoto('');
    if (arquivoAtendimentoRemotoEdicaoInputRef.current) {
      arquivoAtendimentoRemotoEdicaoInputRef.current.value = '';
    }
  };

  const salvarEdicaoAtendimentoRemoto = (id: string) => {
    if (!valorEdicaoArquivoAtendimentoRemoto && !formData.atendimento.remotas.find(item => item.id === id)?.arquivo) {
      toast.warning('Selecione um arquivo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      atendimento: {
        ...prev.atendimento,
        remotas: prev.atendimento.remotas.map(item =>
          item.id === id
            ? {
              ...item,
              arquivo: valorEdicaoArquivoAtendimentoRemoto || item.arquivo,
              arquivoNome: valorEdicaoArquivoAtendimentoRemoto ? valorEdicaoArquivoAtendimentoRemoto.name : item.arquivoNome,
              observacao: valorEdicaoObservacaoAtendimentoRemoto.trim(),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoAtendimentoRemoto();
    toast.success('Atendimento remoto atualizado com sucesso!');
  };

  const handleFileChangeAtendimentoRemotoEdicao = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setValorEdicaoArquivoAtendimentoRemoto(file);
    }
  };

  // Funções para Etapa 11 - Honorários Sentença/Acórdão
  const adicionarHonorarioSentencaAcordao = () => {
    if (!novoProcessoSentencaAcordao.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    // Verifica se já existe
    if (formData.honorarios.sentencaAcordao.some(p => p.numeroProcesso === novoProcessoSentencaAcordao.trim())) {
      toast.warning('Este processo já foi adicionado.');
      return;
    }

    const novoItem: HonorarioSentencaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: novoProcessoSentencaAcordao.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        sentencaAcordao: [...prev.honorarios.sentencaAcordao, novoItem],
      },
    }));

    setNovoProcessoSentencaAcordao('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerHonorarioSentencaAcordao = (id: string) => {
    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        sentencaAcordao: prev.honorarios.sentencaAcordao.filter(item => item.id !== id),
      },
    }));
    toast.success('Processo removido com sucesso!');
  };

  const adicionarMultiplasLinhasHonorariosSentencaAcordao = () => {
    const quantidade = quantidadeLinhas.honorariosSentencaAcordao;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: HonorarioSentencaItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        sentencaAcordao: [...prev.honorarios.sentencaAcordao, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 11 - Honorários Cumprimento
  const adicionarHonorarioCumprimento = () => {
    if (!novoProcessoCumprimento.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    // Verifica se já existe
    if (formData.honorarios.cumprimento.some(p => p.numeroProcesso === novoProcessoCumprimento.trim())) {
      toast.warning('Este processo já foi adicionado.');
      return;
    }

    const novoItem: HonorarioCumprimentoItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: novoProcessoCumprimento.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        cumprimento: [...prev.honorarios.cumprimento, novoItem],
      },
    }));

    setNovoProcessoCumprimento('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerHonorarioCumprimento = (id: string) => {
    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        cumprimento: prev.honorarios.cumprimento.filter(item => item.id !== id),
      },
    }));
    toast.success('Processo removido com sucesso!');
  };

  const adicionarMultiplasLinhasHonorariosCumprimento = () => {
    const quantidade = quantidadeLinhas.honorariosCumprimento;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: HonorarioCumprimentoItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        cumprimento: [...prev.honorarios.cumprimento, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 12 - Ofícios Requisitórios
  const adicionarOficioRequisitorio = () => {
    if (!novoProcessoRequisitorio.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    // Verifica se já existe
    if (formData.oficiosExpedidos.requisitorios.some(p => p.numeroProcesso === novoProcessoRequisitorio.trim())) {
      toast.warning('Este processo já foi adicionado.');
      return;
    }

    const novoItem: OficioRequisitorioItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: novoProcessoRequisitorio.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        requisitorios: [...prev.oficiosExpedidos.requisitorios, novoItem],
      },
    }));

    setNovoProcessoRequisitorio('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerOficioRequisitorio = (id: string) => {
    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        requisitorios: prev.oficiosExpedidos.requisitorios.filter(item => item.id !== id),
      },
    }));
    toast.success('Processo removido com sucesso!');
  };

  const adicionarMultiplasLinhasOficiosRequisitorios = () => {
    const quantidade = quantidadeLinhas.oficiosRequisitorios;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: OficioRequisitorioItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        requisitorios: [...prev.oficiosExpedidos.requisitorios, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 12 - Demais Ofícios
  const adicionarOficioDemais = () => {
    if (!novoProcessoDemaisOficios.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    // Verifica se já existe
    if (formData.oficiosExpedidos.demais.some(p => p.numeroProcesso === novoProcessoDemaisOficios.trim())) {
      toast.warning('Este processo já foi adicionado.');
      return;
    }

    const novoItem: OficioDemaisItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      numeroProcesso: novoProcessoDemaisOficios.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        demais: [...prev.oficiosExpedidos.demais, novoItem],
      },
    }));

    setNovoProcessoDemaisOficios('');
    toast.success('Processo adicionado com sucesso!');
  };

  const removerOficioDemais = (id: string) => {
    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        demais: prev.oficiosExpedidos.demais.filter(item => item.id !== id),
      },
    }));
    toast.success('Processo removido com sucesso!');
  };

  const adicionarMultiplasLinhasOficiosDemais = () => {
    const quantidade = quantidadeLinhas.oficiosDemais;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: OficioDemaisItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        demais: [...prev.oficiosExpedidos.demais, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para edição de Acordos Extrajudiciais
  const iniciarEdicaoAcordoExtrajudicial = (item: AcordoExtrajudicialItem) => {
    setEditandoAcordoExtrajudicial(item.id);
    setValorEdicaoNomeParteAcordo(item.nomeParte);
    setValorEdicaoAnexoAcordo(item.anexo);
  };

  const cancelarEdicaoAcordoExtrajudicial = () => {
    setEditandoAcordoExtrajudicial(null);
    setValorEdicaoNomeParteAcordo('');
    setValorEdicaoAnexoAcordo(null);
  };

  const salvarEdicaoAcordoExtrajudicial = (id: string) => {
    if (!valorEdicaoNomeParteAcordo.trim()) {
      toast.warning('Informe o nome da parte.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      acordosExtrajudiciais: {
        ...prev.acordosExtrajudiciais,
        itens: prev.acordosExtrajudiciais.itens.map(item =>
          item.id === id
            ? {
              ...item,
              nomeParte: apenasLetras(valorEdicaoNomeParteAcordo.trim()),
              anexo: valorEdicaoAnexoAcordo,
              anexoNome: valorEdicaoAnexoAcordo ? valorEdicaoAnexoAcordo.name : item.anexoNome,
            }
            : item
        ),
      },
    }));

    cancelarEdicaoAcordoExtrajudicial();
    toast.success('Acordo extrajudicial atualizado com sucesso!');
  };

  const handleFileChangeAcordoEdicao = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB.');
        return;
      }
      setValorEdicaoAnexoAcordo(file);
    }
  };

  // Funções para edição de Honorários - Sentença/Acórdão
  const iniciarEdicaoHonorarioSentencaAcordao = (item: HonorarioSentencaItem) => {
    setEditandoHonorarioSentencaAcordao(item.id);
    setValorEdicaoProcessoSentencaAcordao(item.numeroProcesso);
  };

  const cancelarEdicaoHonorarioSentencaAcordao = () => {
    setEditandoHonorarioSentencaAcordao(null);
    setValorEdicaoProcessoSentencaAcordao('');
  };

  const salvarEdicaoHonorarioSentencaAcordao = (id: string) => {
    if (!valorEdicaoProcessoSentencaAcordao.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        sentencaAcordao: prev.honorarios.sentencaAcordao.map(item =>
          item.id === id
            ? { ...item, numeroProcesso: valorEdicaoProcessoSentencaAcordao.trim() }
            : item
        ),
      },
    }));

    cancelarEdicaoHonorarioSentencaAcordao();
    toast.success('Honorário atualizado com sucesso!');
  };

  // Funções para edição de Honorários - Cumprimento
  const iniciarEdicaoHonorarioCumprimento = (item: HonorarioCumprimentoItem) => {
    setEditandoHonorarioCumprimento(item.id);
    setValorEdicaoProcessoCumprimento(item.numeroProcesso);
  };

  const cancelarEdicaoHonorarioCumprimento = () => {
    setEditandoHonorarioCumprimento(null);
    setValorEdicaoProcessoCumprimento('');
  };

  const salvarEdicaoHonorarioCumprimento = (id: string) => {
    if (!valorEdicaoProcessoCumprimento.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      honorarios: {
        ...prev.honorarios,
        cumprimento: prev.honorarios.cumprimento.map(item =>
          item.id === id
            ? { ...item, numeroProcesso: valorEdicaoProcessoCumprimento.trim() }
            : item
        ),
      },
    }));

    cancelarEdicaoHonorarioCumprimento();
    toast.success('Honorário atualizado com sucesso!');
  };

  // Funções para edição de Ofícios Expedidos - Requisitórios
  const iniciarEdicaoOficioRequisitorio = (item: OficioRequisitorioItem) => {
    setEditandoOficioRequisitorio(item.id);
    setValorEdicaoProcessoRequisitorio(item.numeroProcesso);
  };

  const cancelarEdicaoOficioRequisitorio = () => {
    setEditandoOficioRequisitorio(null);
    setValorEdicaoProcessoRequisitorio('');
  };

  const salvarEdicaoOficioRequisitorio = (id: string) => {
    if (!valorEdicaoProcessoRequisitorio.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        requisitorios: prev.oficiosExpedidos.requisitorios.map(item =>
          item.id === id
            ? { ...item, numeroProcesso: valorEdicaoProcessoRequisitorio.trim() }
            : item
        ),
      },
    }));

    cancelarEdicaoOficioRequisitorio();
    toast.success('Ofício requisitório atualizado com sucesso!');
  };

  // Funções para edição de Ofícios Expedidos - Demais Ofícios
  const iniciarEdicaoOficioDemais = (item: OficioDemaisItem) => {
    setEditandoOficioDemais(item.id);
    setValorEdicaoProcessoDemaisOficios(item.numeroProcesso);
  };

  const cancelarEdicaoOficioDemais = () => {
    setEditandoOficioDemais(null);
    setValorEdicaoProcessoDemaisOficios('');
  };

  const salvarEdicaoOficioDemais = (id: string) => {
    if (!valorEdicaoProcessoDemaisOficios.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      oficiosExpedidos: {
        ...prev.oficiosExpedidos,
        demais: prev.oficiosExpedidos.demais.map(item =>
          item.id === id
            ? { ...item, numeroProcesso: valorEdicaoProcessoDemaisOficios.trim() }
            : item
        ),
      },
    }));

    cancelarEdicaoOficioDemais();
    toast.success('Ofício atualizado com sucesso!');
  };

  // Funções para Etapa 13 - Designação Especial (Processos)
  const adicionarDesignacaoEspecialProcesso = () => {
    if (!novoTipoPeca.trim()) {
      toast.warning('Informe o tipo de peça.');
      return;
    }
    if (!novoNumeroProcessoDesig.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDefensoriaVara.trim()) {
      toast.warning('Informe a defensoria do processo/vara.');
      return;
    }

    const novoItem: DesignacaoEspecialProcessoItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      tipoPeca: novoTipoPeca.trim(),
      numeroProcesso: novoNumeroProcessoDesig.trim(),
      defensoriaVara: novaDefensoriaVara.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      designacaoEspecialProcessos: {
        ...prev.designacaoEspecialProcessos,
        itens: [...prev.designacaoEspecialProcessos.itens, novoItem],
      },
    }));

    setNovoTipoPeca('');
    setNovoNumeroProcessoDesig('');
    setNovaDefensoriaVara('');
    toast.success('Item adicionado com sucesso!');
  };

  const removerDesignacaoEspecialProcesso = (id: string) => {
    setFormData(prev => ({
      ...prev,
      designacaoEspecialProcessos: {
        ...prev.designacaoEspecialProcessos,
        itens: prev.designacaoEspecialProcessos.itens.filter(item => item.id !== id),
      },
    }));
    toast.success('Item removido com sucesso!');
  };

  const adicionarMultiplasLinhasDesignacaoEspecialProcessos = () => {
    const quantidade = quantidadeLinhas.designacaoEspecialProcessos;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: DesignacaoEspecialProcessoItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        tipoPeca: '',
        numeroProcesso: '',
        defensoriaVara: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      designacaoEspecialProcessos: {
        ...prev.designacaoEspecialProcessos,
        itens: [...prev.designacaoEspecialProcessos.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para Etapa 14 - Designação Especial (Audiências)
  const adicionarDesignacaoEspecialAudiencia = () => {
    if (!novaAudienciaDesig.trim()) {
      toast.warning('Informe a audiência.');
      return;
    }
    if (!novoNumeroProcessoDesigAud.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDataDesigAud) {
      toast.warning('Informe a data da audiência.');
      return;
    }
    if (!novaDefensoriaVaraDesigAud.trim()) {
      toast.warning('Informe a defensoria do processo/vara.');
      return;
    }

    const novoItem: DesignacaoEspecialAudienciaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audiencia: novaAudienciaDesig.trim(),
      numeroProcesso: novoNumeroProcessoDesigAud.trim(),
      data: new Date(novaDataDesigAud),
      defensoriaVara: novaDefensoriaVaraDesigAud.trim(),
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      designacaoEspecialAudiencias: {
        ...prev.designacaoEspecialAudiencias,
        itens: [...prev.designacaoEspecialAudiencias.itens, novoItem],
      },
    }));

    setNovaAudienciaDesig('');
    setNovoNumeroProcessoDesigAud('');
    setNovaDataDesigAud('');
    setNovaDefensoriaVaraDesigAud('');
    toast.success('Item adicionado com sucesso!');
  };

  const removerDesignacaoEspecialAudiencia = (id: string) => {
    setFormData(prev => ({
      ...prev,
      designacaoEspecialAudiencias: {
        ...prev.designacaoEspecialAudiencias,
        itens: prev.designacaoEspecialAudiencias.itens.filter(item => item.id !== id),
      },
    }));
    toast.success('Item removido com sucesso!');
  };

  const adicionarMultiplasLinhasDesignacaoEspecialAudiencias = () => {
    const quantidade = quantidadeLinhas.designacaoEspecialAudiencias;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: DesignacaoEspecialAudienciaItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        audiencia: '',
        numeroProcesso: '',
        data: new Date(),
        defensoriaVara: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      designacaoEspecialAudiencias: {
        ...prev.designacaoEspecialAudiencias,
        itens: [...prev.designacaoEspecialAudiencias.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Funções para edição de Designação Especial (Processos)
  const iniciarEdicaoDesignacaoEspecialProcesso = (item: DesignacaoEspecialProcessoItem) => {
    setEditandoDesignacaoEspecialProcesso(item.id);
    setValorEdicaoTipoPeca(item.tipoPeca);
    setValorEdicaoNumeroProcessoDesig(item.numeroProcesso);
    setValorEdicaoDefensoriaVara(item.defensoriaVara);
  };

  const cancelarEdicaoDesignacaoEspecialProcesso = () => {
    setEditandoDesignacaoEspecialProcesso(null);
    setValorEdicaoTipoPeca('');
    setValorEdicaoNumeroProcessoDesig('');
    setValorEdicaoDefensoriaVara('');
  };

  const salvarEdicaoDesignacaoEspecialProcesso = (id: string) => {
    if (!valorEdicaoTipoPeca.trim()) {
      toast.warning('Informe o tipo de peça.');
      return;
    }
    if (!valorEdicaoNumeroProcessoDesig.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDefensoriaVara.trim()) {
      toast.warning('Informe a defensoria do processo/vara.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      designacaoEspecialProcessos: {
        ...prev.designacaoEspecialProcessos,
        itens: prev.designacaoEspecialProcessos.itens.map(item =>
          item.id === id
            ? {
              ...item,
              tipoPeca: valorEdicaoTipoPeca.trim(),
              numeroProcesso: valorEdicaoNumeroProcessoDesig.trim(),
              defensoriaVara: valorEdicaoDefensoriaVara.trim(),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoDesignacaoEspecialProcesso();
    toast.success('Designação especial (processo) atualizada com sucesso!');
  };

  // Funções para edição de Designação Especial (Audiências)
  const iniciarEdicaoDesignacaoEspecialAudiencia = (item: DesignacaoEspecialAudienciaItem) => {
    setEditandoDesignacaoEspecialAudiencia(item.id);
    setValorEdicaoAudienciaDesig(item.audiencia);
    setValorEdicaoNumeroProcessoDesigAud(item.numeroProcesso);
    setValorEdicaoDataDesigAud(new Date(item.data).toISOString().split('T')[0]);
    setValorEdicaoDefensoriaVaraDesigAud(item.defensoriaVara);
  };

  const cancelarEdicaoDesignacaoEspecialAudiencia = () => {
    setEditandoDesignacaoEspecialAudiencia(null);
    setValorEdicaoAudienciaDesig('');
    setValorEdicaoNumeroProcessoDesigAud('');
    setValorEdicaoDataDesigAud('');
    setValorEdicaoDefensoriaVaraDesigAud('');
  };

  const salvarEdicaoDesignacaoEspecialAudiencia = (id: string) => {
    if (!valorEdicaoAudienciaDesig.trim()) {
      toast.warning('Informe a audiência.');
      return;
    }
    if (!valorEdicaoNumeroProcessoDesigAud.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDataDesigAud) {
      toast.warning('Informe a data da audiência.');
      return;
    }
    if (!valorEdicaoDefensoriaVaraDesigAud.trim()) {
      toast.warning('Informe a defensoria do processo/vara.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      designacaoEspecialAudiencias: {
        ...prev.designacaoEspecialAudiencias,
        itens: prev.designacaoEspecialAudiencias.itens.map(item =>
          item.id === id
            ? {
              ...item,
              audiencia: valorEdicaoAudienciaDesig.trim(),
              numeroProcesso: valorEdicaoNumeroProcessoDesigAud.trim(),
              data: new Date(valorEdicaoDataDesigAud),
              defensoriaVara: valorEdicaoDefensoriaVaraDesigAud.trim(),
            }
            : item
        ),
      },
    }));

    cancelarEdicaoDesignacaoEspecialAudiencia();
    toast.success('Designação especial (audiência) atualizada com sucesso!');
  };

  // Funções para Etapa 10 - Acordos Extrajudiciais
  const adicionarAcordoExtrajudicial = () => {
    if (!novoNomeParteAcordo.trim()) {
      toast.warning('Informe o nome da parte.');
      return;
    }
    if (!novoAnexoAcordo) {
      toast.warning('Selecione um anexo.');
      return;
    }
    if (novoAnexoAcordo.size > 10 * 1024 * 1024) {
      toast.warning('O arquivo deve ter no máximo 10MB.');
      return;
    }

    const novoItem: AcordoExtrajudicialItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nomeParte: novoNomeParteAcordo.trim(),
      anexo: novoAnexoAcordo,
      anexoNome: novoAnexoAcordo.name,
      dataAdicao: new Date(),
    };

    setFormData(prev => ({
      ...prev,
      acordosExtrajudiciais: {
        ...prev.acordosExtrajudiciais,
        itens: [...prev.acordosExtrajudiciais.itens, novoItem],
      },
    }));

    setNovoNomeParteAcordo('');
    setNovoAnexoAcordo(null);
    if (anexoAcordoInputRef.current) {
      anexoAcordoInputRef.current.value = '';
    }
    toast.success('Acordo extrajudicial adicionado com sucesso!');
  };

  const removerAcordoExtrajudicial = (id: string) => {
    setFormData(prev => ({
      ...prev,
      acordosExtrajudiciais: {
        ...prev.acordosExtrajudiciais,
        itens: prev.acordosExtrajudiciais.itens.filter(item => item.id !== id),
      },
    }));
    toast.success('Acordo extrajudicial removido com sucesso!');
  };

  const adicionarMultiplasLinhasAcordosExtrajudiciais = () => {
    const quantidade = quantidadeLinhas.acordosExtrajudiciais;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AcordoExtrajudicialItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        nomeParte: '',
        anexo: null,
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      acordosExtrajudiciais: {
        ...prev.acordosExtrajudiciais,
        itens: [...prev.acordosExtrajudiciais.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  const adicionarMultiplasLinhasAutor = () => {
    if (quantidadeLinhasAutor < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AutorNaturezaItem[] = [];
    for (let i = 0; i < quantidadeLinhasAutor; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        autor: '',
        naturezaAcao: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => ({
      ...prev,
      autorNatureza: {
        itens: [...prev.autorNatureza.itens, ...novosItens],
      },
    }));

    toast.success(`${quantidadeLinhasAutor} linha(s) adicionada(s) com sucesso!`);
  };

  const confirmarExclusaoAutor = (id: string) => {
    removerAutorNatureza(id);
    setConfirmandoExclusaoAutor(null);
  };

  // Função para adicionar múltiplas linhas vazias
  const adicionarMultiplasLinhas = (
    section: ProcessoSection
  ) => {
    const quantidade = quantidadeLinhas[section];
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosProcessos: ProcessoItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosProcessos.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        numeroProcesso: '',
        dataAdicao: new Date(),
      });
    }

    setFormData(prev => {
      const secaoAnterior = prev[section] as { processos: ProcessoItem[] };
      return {
        ...prev,
        [section]: {
          ...secaoAnterior,
          processos: [...secaoAnterior.processos, ...novosProcessos],
        },
      };
    });

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  // Função para confirmar exclusão
  const confirmarExclusao = (
    section: ProcessoSection,
    id: string
  ) => {
    removerProcesso(section, id);
    setConfirmandoExclusao(null);
  };

  // Função auxiliar para renderizar a tabela de processos
  const renderTabelaProcessos = (
    section: ProcessoSection,
    processos: ProcessoItem[]
  ) => {
    if (processos.length === 0) {
      return (
        <div className="text-center text-muted py-4 border rounded">
          <i className="bi bi-inbox fs-1"></i>
          <p className="mb-0 mt-2">Nenhum processo adicionado ainda.</p>
          <small>Adicione os números dos processos acima.</small>
        </div>
      );
    }

    return (
      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th style={{ width: '50px' }}>#</th>
            <th>Número do Processo</th>
            <th style={{ width: '200px' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {processos.map((processo, index) => {
            const estaEditando = editandoProcesso?.section === section && editandoProcesso?.id === processo.id;
            const estaConfirmandoExclusao = confirmandoExclusao?.section === section && confirmandoExclusao?.id === processo.id;
            const refKey = `${section}-${processo.id}`;

            return (
              <tr key={processo.id}>
                <td>{index + 1}</td>
                <td>
                  {estaEditando ? (
                    <Form.Control
                      type="text"
                      size="sm"
                      value={valorEdicao}
                      onChange={(e) => setValorEdicao(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          salvarEdicao(section, processo.id);
                        }
                        if (e.key === 'Escape') {
                          cancelarEdicao();
                        }
                      }}
                      style={{ borderRadius: '8px' }}
                      autoFocus
                    />
                  ) : (
                    processo.numeroProcesso ? (
                      <code>{processo.numeroProcesso}</code>
                    ) : (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Digite o número do processo"
                        style={{ borderRadius: '8px' }}
                        value={processo.numeroProcesso || ''}
                        onChange={(e) => {
                          const valorApenasNumeros = apenasNumeros(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            [section]: {
                              ...prev[section],
                              processos: prev[section].processos.map(p =>
                                p.id === processo.id ? { ...p, numeroProcesso: valorApenasNumeros } : p
                              ),
                            },
                          }));
                        }}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                      />
                    )
                  )}
                </td>
                <td>
                  {estaEditando ? (
                    <div className="d-flex gap-1">
                      <Button style={{ width: '80px', height: '40px' }}
                        variant="success"
                        size="sm"
                        onClick={() => salvarEdicao(section, processo.id)}
                        title="Salvar"
                      >
                        <i className="bi bi-check-lg"> Salvar</i>
                      </Button>
                      <Button style={{ width: '80px', height: '40px' }}
                        variant="secondary"
                        size="sm"
                        onClick={cancelarEdicao}
                        title="Cancelar"
                      >
                        <i className="bi bi-x-lg"> Cancelar</i>
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex gap-1">
                      <Button style={{ width: '80px', height: '40px' }}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => iniciarEdicao(section, processo)}
                        title="Editar"
                      >
                        <i className="bi bi-pencil"> Editar</i>
                      </Button>
                      <Button
                        ref={(el) => { excluirButtonRefs.current[refKey] = el; }}
                        style={{ width: '80px', height: '40px' }}
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setConfirmandoExclusao({ section, id: processo.id })}
                        title="Excluir"
                      >
                        <i className="bi bi-trash"> Excluir</i>
                      </Button>
                      <Overlay
                        show={estaConfirmandoExclusao}
                        target={excluirButtonRefs.current[refKey]}
                        placement="top"
                        containerPadding={20}
                      >
                        <Popover id={`popover-delete-${refKey}`}>
                          <Popover.Header as="h3" className="text-center">
                            Deseja mesmo excluir?
                          </Popover.Header>
                          <Popover.Body className="d-flex gap-2 justify-content-center">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmarExclusao(section, processo.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setConfirmandoExclusao(null)}
                            >
                              Não
                            </Button>
                          </Popover.Body>
                        </Popover>
                      </Overlay>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const meses = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  const getMesLabel = (mesValue: string) => {
    const mes = meses.find(m => m.value === mesValue);
    return mes?.label || mesValue;
  };

  // Carregar dados do defensor e opções de natureza da ação
  useEffect(() => {
    const cpfToUse = state.cpf || getTokenPayload()?.personCPF;

    const carregarDados = async () => {
      try {
        setLoading(true);

        // Modo visualização com ID (ex.: Corregedoria ou Diretor visualizando relatório de outro): carregar só pelo relatório, sem exigir CPF/defensor do usuário logado
        if (isViewMode && relatorioId) {
          const relatorio = await BuscarRelatorioPorId(relatorioId);
          if (!relatorio) {
            toast.error('Relatório não encontrado.');
            navigate(Links.Relatorios);
            return;
          }
          setRelatorioMetaCorregedoria({
            status: relatorio.status,
            corregedoriaAprovadoEm: relatorio.corregedoriaAprovadoEm,
          });
          setDefensorData({
            id: relatorio.defensorId ?? 0,
            pessoa: { nome: relatorio.defensorNome || 'Defensor' },
          } as ReadDefensorResponseDto);
          const baseForm = {
            defensoriaId: relatorio.defensoriaId,
            defensoriaName: relatorio.defensoriaName || '',
            mes: relatorio.mes ? String(relatorio.mes).padStart(2, '0') : '01',
            ano: relatorio.ano ?? new Date().getFullYear(),
          };
          if (relatorio.dados) {
            const dadosRestaurados = restaurarDadosDoFormulario(relatorio.dados);
            setFormData(() => ({ ...dadosRestaurados, ...baseForm }));
          } else {
            setFormData(prev => ({ ...prev, ...baseForm }));
          }
          try {
            const tiposMovimentacoes = await ListarOpcoesMovimentacaoTipo();
            if (tiposMovimentacoes?.objects) {
              setNaturezaAcaoOptions([
                { value: '', label: 'Selecione...' },
                ...tiposMovimentacoes.objects.map((tipo: ReadMovimentacaoTipoDto) => ({
                  value: String(tipo.id),
                  label: tipo.descricao || '',
                })),
              ]);
            }
          } catch {
            // opções de natureza opcionais em modo visualização
          }
          setLoading(false);
          return;
        }

        // Fluxo normal: usuário é o defensor (requer CPF no token)
        if (!cpfToUse) {
          toast.error('CPF do defensor não foi encontrado. Por favor, faça login novamente.');
          navigate(Links.Auth_Obter_Token);
          setLoading(false);
          return;
        }

        const defensor = await BuscarDefensorPorCPF(cpfToUse);
        if (!defensor) {
          toast.error('Defensor não encontrado para o CPF informado.');
          navigate(Links.Relatorios);
          setLoading(false);
          return;
        }

        setDefensorData(defensor);

        const lotacoesData = await BuscarTodasLotacoesDefensor(defensor.id);

        const { vara: varaData } = ObterComarcaVara(defensor, lotacoesData);
        setVara(varaData);

        const titularidadeData = ObterTitularidade(defensor, lotacoesData);
        setTitularidade(titularidadeData);

        // Carregar opções de natureza da ação (tipos de movimentações)
        try {
          const tiposMovimentacoes = await ListarOpcoesMovimentacaoTipo();
          if (tiposMovimentacoes && tiposMovimentacoes.objects) {
            const opcoes: NaturezaAcaoOption[] = [
              { value: '', label: 'Selecione...' },
              ...tiposMovimentacoes.objects.map((tipo: ReadMovimentacaoTipoDto) => ({
                value: String(tipo.id),
                label: tipo.descricao || '',
              })),
            ];
            setNaturezaAcaoOptions(opcoes);
          }
        } catch (errorTipos) {
          console.error('Erro ao carregar tipos de movimentações:', errorTipos);
        }

        try {
          const periodos = await GetPeriodosDoDefensorPorCpf(cpfToUse);
          if (periodos?.objects) setPeriodosDefensor(periodos.objects);
        } catch (errorPeriodos) {
          console.error('Erro ao carregar períodos do defensor:', errorPeriodos);
        }

        if (relatorioId) {
          try {
            const relatorio = await BuscarRelatorioPorId(relatorioId);
            if (relatorio) {
              setMetaReenvioCorregedoria(
                relatorio.status === 'reprovado' &&
                relatorio.dados?.reenvioDiretoCorregedoriaPendente === true,
              );
            }
            if (relatorio?.dados) {
              const dadosRestaurados = restaurarDadosDoFormulario(relatorio.dados);
              setFormData(prev => ({
                ...dadosRestaurados,
                defensoriaId: relatorio.defensoriaId,
                defensoriaName: relatorio.defensoriaName || prev.defensoriaName,
                mes: relatorio.mes ? String(relatorio.mes).padStart(2, '0') : prev.mes,
                ano: relatorio.ano || prev.ano,
              }));
              toast.success('Relatório carregado com sucesso!');
            }
          } catch (errorRelatorio) {
            console.error('Erro ao carregar relatório:', errorRelatorio);
            toast.warning('Não foi possível carregar os dados do relatório. Continuando com formulário vazio.');
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast.error('Erro ao carregar dados do defensor.');
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, [state.cpf, navigate, relatorioId, isViewMode]);

  const getTitularidadeLabel = (tipo: string): { label: string; variant: string } => {
    switch (tipo) {
      case 'titular':
        return { label: 'Titular', variant: 'success' };
      case 'substituto':
        return { label: 'Substituto', variant: 'info' };
      case 'cedido':
        return { label: 'Cedido', variant: 'warning' };
      default:
        return { label: 'Não definido', variant: 'secondary' };
    }
  };

  // Funções de navegação do wizard
  const handleNextStep = async () => {
    if (currentStep < WIZARD_STEPS.length) {
      // Salvar automaticamente antes de trocar de etapa (apenas se não estiver em modo visualização)
      if (!isViewMode) {
        await autoSave();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = async () => {
    if (currentStep > 1) {
      // Salvar automaticamente antes de trocar de etapa (apenas se não estiver em modo visualização)
      if (!isViewMode) {
        await autoSave();
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGoToStep = async (step: number) => {
    // Salvar automaticamente antes de trocar de etapa (apenas se não estiver em modo visualização)
    if (!isViewMode) {
      await autoSave();
    }
    setCurrentStep(step);
  };

  // Verifica se o mês do relatório já terminou
  const mesRelatorioTerminado = () => {
    const mesRelatorio = parseInt(formData.mes);
    const anoRelatorio = formData.ano;
    const dataAtual = new Date();
    const anoAtual = dataAtual.getFullYear();
    const mesAtual = dataAtual.getMonth() + 1; // getMonth() retorna 0-11

    // Se o ano do relatório for menor que o ano atual, o mês já terminou
    if (anoRelatorio < anoAtual) {
      return true;
    }

    // Se o ano do relatório for igual ao ano atual, verificar se o mês já passou
    if (anoRelatorio === anoAtual) {
      // Se o mês do relatório for menor que o mês atual, já terminou
      if (mesRelatorio < mesAtual) {
        return true;
      }
      // Se estamos no mesmo mês, ainda não terminou
      if (mesRelatorio === mesAtual) {
        return false;
      }
      // Se o mês do relatório for maior que o mês atual, ainda não terminou (futuro)
      return false;
    }

    // Se o ano do relatório for maior que o ano atual, ainda não terminou (futuro)
    return false;
  };

  // Função auxiliar para converter File para base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Função auxiliar para converter base64 para File
  const base64ToFile = (base64: string, filename: string, mimeType: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || mimeType;
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Função para preparar dados do formulário para salvar (converter Files para base64)
  const prepararDadosParaSalvar = async (dados: RelatorioCivelFormData): Promise<any> => {
    // Processar arquivos ANTES da serialização JSON (para preservar File objects)
    // Criar estrutura de dados para processar
    const dadosParaProcessar: any = {
      ...dados,
      comparecimento: {
        ...dados.comparecimento,
        diasStatus: dados.comparecimento.diasStatus.map(d => ({ ...d })),
      },
      acordosExtrajudiciais: {
        ...dados.acordosExtrajudiciais,
        itens: dados.acordosExtrajudiciais.itens.map(i => ({ ...i })),
      },
      atendimento: {
        ...dados.atendimento,
        presenciais: dados.atendimento.presenciais.map(a => ({ ...a })),
        remotas: dados.atendimento.remotas.map(a => ({ ...a })),
      },
    };

    // Converter arquivos em diasStatus (justificativas)
    if (dadosParaProcessar.comparecimento?.diasStatus) {
      for (const diaStatus of dadosParaProcessar.comparecimento.diasStatus) {
        if (diaStatus.justificativaArquivo instanceof File) {
          diaStatus.justificativaArquivoBase64 = await fileToBase64(diaStatus.justificativaArquivo);
          diaStatus.justificativaArquivoNome = diaStatus.justificativaArquivo.name;
        }
        diaStatus.justificativaArquivo = null;
      }
    }

    // Converter arquivos em acordosExtrajudiciais
    if (dadosParaProcessar.acordosExtrajudiciais?.itens) {
      for (const acordo of dadosParaProcessar.acordosExtrajudiciais.itens) {
        if (acordo.anexo instanceof File) {
          acordo.anexoBase64 = await fileToBase64(acordo.anexo);
          acordo.anexoNome = acordo.anexo.name;
        }
        acordo.anexo = null;
      }
    }

    // Converter arquivos em atendimentos presenciais (EXATAMENTE como em remotos)
    if (dadosParaProcessar.atendimento?.presenciais) {
      for (const atendimento of dadosParaProcessar.atendimento.presenciais) {
        if (atendimento.arquivo instanceof File) {
          // Arquivo novo ou modificado - converter para base64
          atendimento.arquivoBase64 = await fileToBase64(atendimento.arquivo);
          atendimento.arquivoNome = atendimento.arquivo.name;
        }
        // Se não é um File object novo, o arquivoBase64 já deve estar preservado
        // (foi mantido na função de restauração)
        atendimento.arquivo = null;
      }
    }

    // Converter arquivos em atendimentos remotos
    if (dadosParaProcessar.atendimento?.remotas) {
      for (const atendimento of dadosParaProcessar.atendimento.remotas) {
        if (atendimento.arquivo instanceof File) {
          // Arquivo novo ou modificado - converter para base64
          atendimento.arquivoBase64 = await fileToBase64(atendimento.arquivo);
          atendimento.arquivoNome = atendimento.arquivo.name;
        }
        // Se não é um File object novo, o arquivoBase64 já deve estar preservado
        // (foi mantido na função de restauração)
        atendimento.arquivo = null;
      }
    }

    // Agora serializar para JSON (File objects já foram convertidos ou limpos)
    const dadosSerializados = JSON.parse(JSON.stringify(dadosParaProcessar));

    return dadosSerializados;
  };

  // Função para restaurar dados do formulário (converter base64 para Files e strings para Dates)
  const restaurarDadosDoFormulario = (dados: any): RelatorioCivelFormData => {
    const dadosRestaurados = JSON.parse(JSON.stringify(dados));

    // Restaurar arquivos e datas em diasStatus (justificativas) e normalizar mes/ano
    if (dadosRestaurados.comparecimento?.diasStatus) {
      for (const diaStatus of dadosRestaurados.comparecimento.diasStatus) {
        if (diaStatus.justificativaArquivoBase64) {
          diaStatus.justificativaArquivo = base64ToFile(
            diaStatus.justificativaArquivoBase64,
            diaStatus.justificativaArquivoNome || 'anexo.pdf',
            'application/pdf'
          );
          delete diaStatus.justificativaArquivoBase64;
        }
        // Normalizar mes/ano para formato consistente (todas as semanas do calendário)
        if (diaStatus.mes != null) diaStatus.mes = String(diaStatus.mes).padStart(2, '0');
        if (diaStatus.ano != null) diaStatus.ano = Number(diaStatus.ano);
      }
    }

    // Restaurar arquivos em acordosExtrajudiciais
    if (dadosRestaurados.acordosExtrajudiciais?.itens) {
      for (const acordo of dadosRestaurados.acordosExtrajudiciais.itens) {
        if (acordo.anexoBase64) {
          acordo.anexo = base64ToFile(
            acordo.anexoBase64,
            acordo.anexoNome || 'anexo.pdf',
            'application/pdf'
          );
          delete acordo.anexoBase64;
        }
      }
    }

    // Restaurar arquivos em atendimentos presenciais (EXATAMENTE como em remotos)
    if (dadosRestaurados.atendimento?.presenciais) {
      for (const atendimento of dadosRestaurados.atendimento.presenciais) {
        if (atendimento.arquivoBase64) {
          // Criar File object para exibição/edição
          atendimento.arquivo = base64ToFile(
            atendimento.arquivoBase64,
            atendimento.arquivoNome || 'anexo.pdf',
            'application/pdf'
          );
          // NÃO deletar arquivoBase64 - precisamos preservá-lo para salvar novamente
          // O arquivoBase64 será usado se o arquivo não for modificado
          // IMPORTANTE: manter arquivoBase64 preservado (igual ao que funciona em remotos)
        }
        // Converter data de string para Date (se houver campo data)
        if (atendimento.data && typeof atendimento.data === 'string') {
          atendimento.data = new Date(atendimento.data);
        }
      }
    }

    // Restaurar arquivos em atendimentos remotos
    if (dadosRestaurados.atendimento?.remotas) {
      for (const atendimento of dadosRestaurados.atendimento.remotas) {
        if (atendimento.arquivoBase64) {
          // Criar File object para exibição/edição
          atendimento.arquivo = base64ToFile(
            atendimento.arquivoBase64,
            atendimento.arquivoNome || 'anexo.pdf',
            'application/pdf'
          );
          // NÃO deletar arquivoBase64 - precisamos preservá-lo para salvar novamente
          // O arquivoBase64 será usado se o arquivo não for modificado
        }
        // Converter data de string para Date
        if (atendimento.data && typeof atendimento.data === 'string') {
          atendimento.data = new Date(atendimento.data);
        }
      }
    }

    // Converter datas em audiências presenciais
    if (dadosRestaurados.audiencias?.presenciais) {
      for (const audiencia of dadosRestaurados.audiencias.presenciais) {
        if (audiencia.data && typeof audiencia.data === 'string') {
          audiencia.data = new Date(audiencia.data);
        }
      }
    }

    // Converter datas em audiências remotas
    if (dadosRestaurados.audiencias?.remotas) {
      for (const audiencia of dadosRestaurados.audiencias.remotas) {
        if (audiencia.data && typeof audiencia.data === 'string') {
          audiencia.data = new Date(audiencia.data);
        }
      }
    }

    // Converter datas em designação especial (audiências)
    if (dadosRestaurados.designacaoEspecialAudiencias?.itens) {
      for (const item of dadosRestaurados.designacaoEspecialAudiencias.itens) {
        if (item.data && typeof item.data === 'string') {
          item.data = new Date(item.data);
        }
      }
    }

    // Converter datas em acordos extrajudiciais
    if (dadosRestaurados.acordosExtrajudiciais?.itens) {
      for (const acordo of dadosRestaurados.acordosExtrajudiciais.itens) {
        if (acordo.dataAdicao && typeof acordo.dataAdicao === 'string') {
          acordo.dataAdicao = new Date(acordo.dataAdicao);
        }
      }
    }

    // Converter datas em autorNatureza
    if (dadosRestaurados.autorNatureza?.itens) {
      for (const item of dadosRestaurados.autorNatureza.itens) {
        if (item.dataAdicao && typeof item.dataAdicao === 'string') {
          item.dataAdicao = new Date(item.dataAdicao);
        }
      }
    }

    // Converter datas em honorários
    if (dadosRestaurados.honorarios?.sentencaAcordao) {
      for (const item of dadosRestaurados.honorarios.sentencaAcordao) {
        if (item.dataAdicao && typeof item.dataAdicao === 'string') {
          item.dataAdicao = new Date(item.dataAdicao);
        }
      }
    }
    if (dadosRestaurados.honorarios?.cumprimento) {
      for (const item of dadosRestaurados.honorarios.cumprimento) {
        if (item.dataAdicao && typeof item.dataAdicao === 'string') {
          item.dataAdicao = new Date(item.dataAdicao);
        }
      }
    }

    // Converter datas em ofícios expedidos
    if (dadosRestaurados.oficiosExpedidos?.requisitorios) {
      for (const item of dadosRestaurados.oficiosExpedidos.requisitorios) {
        if (item.dataAdicao && typeof item.dataAdicao === 'string') {
          item.dataAdicao = new Date(item.dataAdicao);
        }
      }
    }
    if (dadosRestaurados.oficiosExpedidos?.demais) {
      for (const item of dadosRestaurados.oficiosExpedidos.demais) {
        if (item.dataAdicao && typeof item.dataAdicao === 'string') {
          item.dataAdicao = new Date(item.dataAdicao);
        }
      }
    }

    // Converter datas em designação especial (processos)
    // Não há datas neste tipo, então não precisa converter

    return dadosRestaurados as RelatorioCivelFormData;
  };

  const podeCorregedoriaAgirComparecimento =
    isViewMode &&
    origemVisualizacao === 'corregedoria' &&
    !!relatorioId &&
    relatorioMetaCorregedoria?.status === 'aprovado' &&
    !relatorioMetaCorregedoria?.corregedoriaAprovadoEm;

  const diasComparecimentoCorregedoriaPendentes = useMemo(() => {
    const dias = formData.comparecimento.diasStatus || [];
    return dias.filter(
      (d) =>
        (d.status === 'remoto' && d.remotoValidado !== true) ||
        (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true),
    );
  }, [formData.comparecimento.diasStatus]);

  const aplicarDecisaoCorregedoriaNoDia = async (
    dia: DiaComparecimentoStatus,
    acao: ComparecimentoDecisaoAcao,
  ) => {
    if (!relatorioId || !podeCorregedoriaAgirComparecimento) return;
    const mes = String(dia.mes ?? formData.mes).padStart(2, '0');
    const ano = dia.ano ?? formData.ano;
    const rowKey = `${dia.dia}-${mes}-${ano}`;
    setCorregedoriaDecisaoDiaLoading(rowKey);
    try {
      await AplicarDecisoesComparecimentoCorregedoria(relatorioId, [
        { dia: dia.dia, mes, ano, acao },
      ]);
      const relatorio = await BuscarRelatorioPorId(relatorioId);
      if (relatorio?.dados) {
        const dadosRestaurados = restaurarDadosDoFormulario(relatorio.dados);
        setFormData((prev) => ({
          ...dadosRestaurados,
          defensoriaId: relatorio.defensoriaId,
          defensoriaName: relatorio.defensoriaName || prev.defensoriaName,
          mes: relatorio.mes ? String(relatorio.mes).padStart(2, '0') : prev.mes,
          ano: relatorio.ano ?? prev.ano,
        }));
      }
      if (relatorio) {
        setRelatorioMetaCorregedoria({
          status: relatorio.status,
          corregedoriaAprovadoEm: relatorio.corregedoriaAprovadoEm,
        });
      }
      toast.success('Decisão da Corregedoria registrada.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao registrar decisão.');
    } finally {
      setCorregedoriaDecisaoDiaLoading(null);
    }
  };

  // Função para auto-save (salvar automaticamente ao trocar de etapa)
  const autoSave = async () => {
    if (!defensorData || !formData.defensoriaId) {
      return; // Não salva se não tiver dados básicos
    }

    try {
      const dadosParaSalvar = await prepararDadosParaSalvar(formData);
      const resultado = await AutoSaveRelatorio(
        defensorData.id,
        formData.defensoriaId,
        formData.mes,
        formData.ano,
        'civil',
        dadosParaSalvar
      );

      // Atualizar o ID do relatório se foi criado
      if (resultado.id && !relatorioId) {
        setRelatorioId(resultado.id);
        // Atualizar URL com o ID
        const params = new URLSearchParams(searchParams.toString());
        params.set('id', String(resultado.id));
        navigate(`${Links.Relatorios_Criar}?${params.toString()}`, { replace: true });
      }
    } catch (error) {
      console.error('Erro ao salvar automaticamente:', error);
      // Não mostra toast de erro para auto-save silencioso
    }
  };

  const handleSave = async (apenasRascunho: boolean = true) => {
    try {
      setSaving(true);

      if (!defensorData) {
        toast.error('Dados do defensor não carregados.');
        return;
      }

      if (!formData.defensoriaId) {
        toast.error('Defensoria não informada.');
        return;
      }

      const dadosParaSalvar = await prepararDadosParaSalvar(formData);

      if (!apenasRascunho && metaReenvioCorregedoria && relatorioId) {
        await SalvarRelatorio(
          defensorData.id,
          formData.defensoriaId,
          formData.mes,
          formData.ano,
          'civil',
          dadosParaSalvar,
          'reprovado',
          relatorioId,
        );
        await EnviarDiretoCorregedoriaDefensor(relatorioId);
        toast.success('Relatório encaminhado diretamente à Corregedoria.');
        navigate(Links.Relatorios);
        return;
      }

      const status = apenasRascunho ? 'rascunho' : 'enviado';

      const resultado = await SalvarRelatorio(
        defensorData.id,
        formData.defensoriaId,
        formData.mes,
        formData.ano,
        'civil',
        dadosParaSalvar,
        status,
        relatorioId || undefined
      );

      // Atualizar o ID do relatório se foi criado
      if (resultado.id && !relatorioId) {
        setRelatorioId(resultado.id);
      }

      if (apenasRascunho) {
        toast.success('Relatório salvo como rascunho!');
      } else {
        toast.success('Relatório enviado com sucesso!');
      }

      navigate(Links.Relatorios);
    } catch (error: any) {
      console.error('Erro ao salvar relatório:', error);
      const mensagem = error?.response?.data?.message || 'Erro ao salvar relatório.';
      toast.error(mensagem);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isViewMode) {
      navigate(Links.Relatorios);
      return;
    }

    if (window.confirm('Tem certeza que deseja cancelar? As alterações não salvas serão perdidas.')) {
      navigate(Links.Relatorios);
    }
  };

  // Funções auxiliares para o calendário

  // Retorna os feriados que caem exatamente na data informada (considerando intervalos de início/fim)
  const getFeriadosNaData = (data: Date): ReadHolidayResponseDto[] => {
    return feriados.filter((feriado) => {
      const dataInicio = new Date(feriado.inicio);
      const dataFim = feriado.fim ? new Date(feriado.fim) : null;

      if (
        dataInicio.getFullYear() === data.getFullYear() &&
        dataInicio.getMonth() === data.getMonth() &&
        dataInicio.getDate() === data.getDate()
      ) {
        return true;
      }

      if (dataFim) {
        const dataSelecionadaTime = new Date(
          data.getFullYear(),
          data.getMonth(),
          data.getDate()
        ).getTime();
        const inicioTime = new Date(
          dataInicio.getFullYear(),
          dataInicio.getMonth(),
          dataInicio.getDate()
        ).getTime();
        const fimTime = new Date(
          dataFim.getFullYear(),
          dataFim.getMonth(),
          dataFim.getDate()
        ).getTime();

        return dataSelecionadaTime >= inicioTime && dataSelecionadaTime <= fimTime;
      }

      return false;
    });
  };
  const gerarCalendarioMensal = (mes: string, ano: number) => {
    const mesNum = parseInt(mes) - 1; // JavaScript usa mês 0-indexed
    const primeiroDia = new Date(ano, mesNum, 1);
    const ultimoDia = new Date(ano, mesNum + 1, 0);
    const diasNoMes = ultimoDia.getDate();
    const diaDaSemanaInicio = primeiroDia.getDay(); // 0 = Domingo

    // Calcular mês anterior e posterior
    const mesAnterior = mesNum === 0 ? 11 : mesNum - 1;
    const anoAnterior = mesNum === 0 ? ano - 1 : ano;
    const diasNoMesAnterior = new Date(anoAnterior, mesAnterior + 1, 0).getDate();

    const mesPosterior = mesNum === 11 ? 0 : mesNum + 1;
    const anoPosterior = mesNum === 11 ? ano + 1 : ano;

    // Criar matriz de semanas
    const semanas: (DiaCalendario | null)[][] = [];
    let semanaAtual: (DiaCalendario | null)[] = [];

    // Preencher dias do mês anterior antes do primeiro dia do mês
    let diaAnterior = diasNoMesAnterior - diaDaSemanaInicio + 1;
    for (let i = 0; i < diaDaSemanaInicio; i++) {
      semanaAtual.push({
        dia: diaAnterior,
        mes: String(mesAnterior + 1).padStart(2, '0'),
        ano: anoAnterior,
        isMesAtual: false,
      });
      diaAnterior++;
    }

    // Preencher os dias do mês atual (sempre mes/ano normalizados para todas as linhas)
    const mesNorm = String(mes).padStart(2, '0');
    const anoNorm = Number(ano);
    for (let dia = 1; dia <= diasNoMes; dia++) {
      semanaAtual.push({
        dia: dia,
        mes: mesNorm,
        ano: anoNorm,
        isMesAtual: true,
      });
      if (semanaAtual.length === 7) {
        semanas.push(semanaAtual);
        semanaAtual = [];
      }
    }

    // Preencher dias do mês posterior após o último dia do mês
    if (semanaAtual.length > 0) {
      let diaPosterior = 1;
      while (semanaAtual.length < 7) {
        semanaAtual.push({
          dia: diaPosterior,
          mes: String(mesPosterior + 1).padStart(2, '0'),
          ano: anoPosterior,
          isMesAtual: false,
        });
        diaPosterior++;
      }
      semanas.push(semanaAtual);
    }

    return semanas;
  };

  // Verificar se o dia é útil (não é fim de semana nem feriado/bloqueio)
  const isDiaUtil = (dia: number, mes: string, ano: number) => {
    const data = new Date(ano, parseInt(mes) - 1, dia);
    const diaSemana = data.getDay();
    if (diaSemana === 0 || diaSemana === 6) {
      return false; // Não é domingo nem sábado
    }

    const feriadosNaData = getFeriadosNaData(data);
    if (feriadosNaData.length > 0) {
      return false; // Dia de feriado/bloqueio não é dia útil
    }

    return true;
  };

  // Verificar se o dia é futuro (bloqueado para marcação)
  const isDiaFuturo = (dia: number, mes: string, ano: number) => {
    const dataAtual = new Date();
    dataAtual.setHours(0, 0, 0, 0);
    const dataDia = new Date(ano, parseInt(mes) - 1, dia);
    dataDia.setHours(0, 0, 0, 0);
    return dataDia > dataAtual;
  };

  // Verificar se um dia tem impedimento (férias, abono, folga, licença, etc.)
  const getImpedimentoDia = (dia: number, mes: string, ano: number): ReadPeriodoDto | null => {
    const dataDia = new Date(ano, parseInt(mes) - 1, dia);
    dataDia.setHours(0, 0, 0, 0);

    for (const periodo of periodosDefensor) {
      const dataInicial = new Date(periodo.data_inicial);
      const dataFinal = new Date(periodo.data_final);
      dataInicial.setHours(0, 0, 0, 0);
      dataFinal.setHours(23, 59, 59, 999);

      if (dataDia >= dataInicial && dataDia <= dataFinal) {
        return periodo;
      }
    }

    return null;
  };

  // Obter status de um dia específico (compara mês/ano normalizados para evitar "02" vs "2")
  const getStatusDia = (dia: number, mes?: string, ano?: number): DiaComparecimentoStatus | undefined => {
    const mesBusca = String(mes || formData.mes).padStart(2, '0');
    const anoBusca = Number(ano ?? formData.ano);
    return formData.comparecimento.diasStatus.find(d => {
      if (d.dia !== dia) return false;
      const dMes = d.mes === undefined ? mesBusca : String(d.mes).padStart(2, '0');
      const dAno = d.ano === undefined ? anoBusca : Number(d.ano);
      return dMes === mesBusca && dAno === anoBusca;
    });
  };

  // Atualizar status de um dia
  const atualizarStatusDia = (
    dia: number,
    status: 'presente' | 'ausente' | 'remoto' | 'impedimento' | 'nao_marcado',
    justificativaTexto?: string,
    justificativaArquivo?: File | null,
    tipoImpedimento?: TipoPeriodoDescanso,
    impedimentoDescricao?: string,
    mes?: string, // Mês do dia (opcional, padrão é o mês do relatório)
    ano?: number  // Ano do dia (opcional, padrão é o ano do relatório)
  ) => {
    // Verificar se o dia pertence ao período do relatório atual (comparação normalizada)
    const mesDia = String(mes || formData.mes).padStart(2, '0');
    const anoDia = Number(ano ?? formData.ano);
    const mesRel = String(formData.mes).padStart(2, '0');
    const anoRel = Number(formData.ano);
    if (mesDia !== mesRel || anoDia !== anoRel) {
      toast.warning(`Este dia pertence ao período de ${getMesLabel(mesDia)}/${anoDia} e não pode ser editado no relatório de ${getMesLabel(formData.mes)}/${formData.ano}.`);
      return;
    }

    // Não há limite máximo de presenças por semana, apenas mínimo obrigatório.

    setFormData(prev => {
      const diasStatus = [...prev.comparecimento.diasStatus];
      const mesPrev = String(prev.mes).padStart(2, '0');
      const anoPrev = Number(prev.ano);
      const index = diasStatus.findIndex(d => {
        if (d.dia !== dia) return false;
        const dMes = d.mes === undefined ? mesPrev : String(d.mes).padStart(2, '0');
        const dAno = d.ano === undefined ? anoPrev : Number(d.ano);
        return dMes === mesPrev && dAno === anoPrev;
      });

      if (status === 'nao_marcado') {
        // Remover o dia da lista
        if (index >= 0) {
          diasStatus.splice(index, 1);
        }
      } else {
        const novoStatus: DiaComparecimentoStatus = {
          dia,
          mes: String(prev.mes).padStart(2, '0'),
          ano: Number(prev.ano),
          status,
          justificativaTexto: justificativaTexto || '',
          justificativaArquivo: justificativaArquivo || null,
          justificativaArquivoNome: justificativaArquivo?.name || '',
          tipoImpedimento,
          impedimentoDescricao,
        };

        if (index >= 0) {
          diasStatus[index] = novoStatus;
        } else {
          diasStatus.push(novoStatus);
        }
      }

      // Contar dias presentes (apenas 'presente'; 'remoto' só conta após validação da corregedoria)
      const diasPresentes = diasStatus.filter(d => d.status === 'presente').length;

      return {
        ...prev,
        comparecimento: {
          ...prev.comparecimento,
          diasStatus: diasStatus.sort((a, b) => a.dia - b.dia),
          diasComparecimento: diasPresentes,
        },
      };
    });
  };

  // Abrir modal de justificativa
  const abrirJustificativaModal = (dia: number, mes?: string, ano?: number) => {
    const mesBusca = mes || formData.mes;
    const anoBusca = ano || formData.ano;

    // Verificar se o dia pertence ao período do relatório atual
    if (mesBusca !== formData.mes || anoBusca !== formData.ano) {
      toast.warning(`Este dia pertence ao período de ${getMesLabel(mesBusca)}/${anoBusca} e não pode ser editado no relatório de ${getMesLabel(formData.mes)}/${formData.ano}.`);
      return;
    }
    const status = getStatusDia(dia, mesBusca, anoBusca);
    setJustificativaTextoTemp(status?.justificativaTexto || '');
    setJustificativaArquivoTemp(status?.justificativaArquivo || null);
    // Usar setTimeout para evitar que o rootClose feche imediatamente
    setTimeout(() => {
      setJustificativaModalDia(dia);
    }, 100);
  };

  // Salvar justificativa
  const salvarJustificativa = () => {
    if (justificativaModalDia !== null) {
      // O dia já foi validado ao abrir o modal, então podemos usar o mês/ano do relatório atual
      atualizarStatusDia(
        justificativaModalDia,
        'ausente',
        justificativaTextoTemp,
        justificativaArquivoTemp,
        undefined,
        undefined,
        formData.mes,
        formData.ano
      );
      setJustificativaModalDia(null);
      setJustificativaTextoTemp('');
      setJustificativaArquivoTemp(null);
    }
  };

  // Cancelar justificativa
  const cancelarJustificativa = () => {
    setJustificativaModalDia(null);
    setJustificativaTextoTemp('');
    setJustificativaArquivoTemp(null);
  };

  // Handler para mudança de arquivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setJustificativaArquivoTemp(file);
  };

  const getNomeSemana = (index: number) => {
    const nomes = ['1ª Semana', '2ª Semana', '3ª Semana', '4ª Semana', '5ª Semana', '6ª Semana'];
    return nomes[index] || `${index + 1}ª Semana`;
  };

  // Contar dias úteis (Seg a Sex) do mês do relatório em uma semana
  const contarDiasUteisSemana = (semana: (DiaCalendario | null)[]) => {
    return semana.filter(dia =>
      dia !== null && dia.isMesAtual && isDiaUtil(dia.dia, dia.mes, dia.ano)
    ).length;
  };

  // Presença mínima obrigatória por semana (apenas dias do mês do relatório):
  // 4 ou 5 dias úteis → 3 obrigatórios (2 opcionais); 3 ou 2 dias úteis → 2 obrigatórios; 1 dia útil → 1 obrigatório
  const getPresencaMinimaObrigatoria = (diasUteis: number): number => {
    if (diasUteis >= 4) return 3;  // 4 ou 5 dias úteis → 3 presenças obrigatórias
    if (diasUteis === 3 || diasUteis === 2) return 2;  // 3 ou 2 dias úteis → 2 obrigatórios
    if (diasUteis === 1) return 1;  // 1 dia útil → 1 obrigatório
    return 0;
  };

  // Verificar se a semana já passou (último dia da linha do calendário já é anterior a hoje)
  const semanaJaPassou = (semana: (DiaCalendario | null)[]): boolean => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const diasValidos = semana.filter((d): d is DiaCalendario => d !== null);
    if (diasValidos.length === 0) return false;
    let ultimoDia = diasValidos[0];
    for (const d of diasValidos) {
      const dataD = new Date(d.ano, parseInt(d.mes) - 1, d.dia);
      const dataUlt = new Date(ultimoDia.ano, parseInt(ultimoDia.mes) - 1, ultimoDia.dia);
      if (dataD > dataUlt) ultimoDia = d;
    }
    const dataUltimo = new Date(ultimoDia.ano, parseInt(ultimoDia.mes) - 1, ultimoDia.dia);
    dataUltimo.setHours(0, 0, 0, 0);
    return dataUltimo < hoje;
  };

  // Contar impedimentos no período selecionado
  const contarImpedimentosNoPeriodo = (): { total: number; dias: number[] } => {
    const diasComImpedimento: number[] = [];
    const ultimoDiaMes = new Date(formData.ano, parseInt(formData.mes), 0).getDate();

    for (let dia = 1; dia <= ultimoDiaMes; dia++) {
      const impedimento = getImpedimentoDia(dia, formData.mes, formData.ano);
      if (impedimento) {
        diasComImpedimento.push(dia);
      }
    }

    return { total: diasComImpedimento.length, dias: diasComImpedimento };
  };

  // Estilo minimalista (alinhado aos demais componentes de relatório)
  const cardStyle = {
    border: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px',
    boxShadow: RELATORIO_CORES.SOMBRA_CARD,
  };
  const headerStyle = {
    backgroundColor: RELATORIO_CORES.CINZA_CLARO,
    color: RELATORIO_CORES.PRIMARIA,
    borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px 8px 0 0',
  };

  // Renderização das etapas
  const renderStep1 = () => {
    const mesNorm = String(formData.mes).padStart(2, '0');
    const calendario = gerarCalendarioMensal(mesNorm, formData.ano);
    const diasStatus = formData.comparecimento.diasStatus || [];
    // Presença: apenas 'presente'; 'remoto' só conta quando corregedoria validou (remotoValidado === true)
    const diasPresentes = diasStatus.filter(
      d => d.status === 'presente' || (d.status === 'remoto' && d.remotoValidado === true)
    ).length;
    // `contarImpedimentosNoPeriodo` é usado em outros trechos da etapa; aqui não precisamos armazenar em variável.
    contarImpedimentosNoPeriodo();
    const diasAverbacaoPendentes = diasStatus.some(
      (d) =>
        (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true) ||
        (d.status === 'remoto' && d.remotoValidado !== true),
    );

    return (
      <Fragment>
        {/* Legenda com quadradinhos coloridos */}
        <Card className="mb-3" style={cardStyle}>
          <Card.Body className="py-2">
            <div className="d-flex flex-wrap gap-4 align-items-center justify-content-center">
              <span className="d-flex align-items-center gap-1">
                <span style={{ width: 14, height: 14, backgroundColor: '#198754', borderRadius: 2 }} aria-hidden />
                <small>Presente</small>
              </span>
              <span className="d-flex align-items-center gap-1">
                <span style={{ width: 14, height: 14, backgroundColor: '#ffc107', borderRadius: 2 }} aria-hidden />
                <small>Ausente</small>
              </span>
              <span className="d-flex align-items-center gap-1">
                <span style={{ width: 14, height: 14, backgroundColor: '#0d6efd', borderRadius: 2 }} aria-hidden />
                <small>Remoto</small>
              </span>
              <span className="d-flex align-items-center gap-1">
                <span style={{ width: 14, height: 14, backgroundColor: '#6c757d', borderRadius: 2 }} aria-hidden />
                <small>Impedimento</small>
              </span>
            </div>
          </Card.Body>
        </Card>

        {isViewMode && origemVisualizacao === 'corregedoria' && diasAverbacaoPendentes && (
          <div className="alert alert-warning" role="alert" style={{ borderLeft: '5px solid #ffc107' }}>
            <i className="bi bi-exclamation-triangle text-warning blink-tri me-2" aria-hidden />
            <strong>Atenção:</strong> existem dias marcados como <strong>Remoto</strong> ou <strong>Ausente</strong> sem decisão da Corregedoria. Use a tabela abaixo ou o fluxo na lista de relatórios antes do aval final.
          </div>
        )}

        {podeCorregedoriaAgirComparecimento && (
          <Card className="mb-3" style={cardStyle}>
            <Card.Header style={headerStyle}>
              <h6 className="mb-0 text-center">Corregedoria — decisão por dia (remoto / ausência)</h6>
            </Card.Header>
            <Card.Body>
              {diasComparecimentoCorregedoriaPendentes.length === 0 ? (
                <p className="mb-0 text-muted small">
                  Não há dias remoto ou ausente pendentes. Você pode voltar à lista da Corregedoria e registrar o aval final.
                </p>
              ) : (
                <div className="table-responsive">
                  <Table bordered size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Justificativa</th>
                        <th style={{ minWidth: 200 }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diasComparecimentoCorregedoriaPendentes.map((d) => {
                        const mesR = String(d.mes ?? formData.mes).padStart(2, '0');
                        const anoR = d.ano ?? formData.ano;
                        const rowKey = `${d.dia}-${mesR}-${anoR}`;
                        const busy = corregedoriaDecisaoDiaLoading === rowKey;
                        const labelData = `${d.dia} ${getMesLabel(mesR)}/${anoR}`;
                        return (
                          <tr key={`${d.status}-${rowKey}`}>
                            <td>{labelData}</td>
                            <td>{d.status === 'remoto' ? 'Remoto' : 'Ausente'}</td>
                            <td>
                              <small className="text-muted">{d.justificativaTexto?.trim() || '—'}</small>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {d.status === 'remoto' ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="success"
                                      disabled={busy}
                                      onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_validar')}
                                    >
                                      {busy ? <Spinner animation="border" size="sm" /> : 'Validar presença'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      disabled={busy}
                                      onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_rejeitar')}
                                    >
                                      Anular dia
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="success"
                                      disabled={busy}
                                      onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_aceitar')}
                                    >
                                      {busy ? <Spinner animation="border" size="sm" /> : 'Aceitar ausência'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      disabled={busy}
                                      onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_rejeitar')}
                                    >
                                      Anular dia
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Calendário Mensal */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <div className="d-flex align-items-center">
              <h6 className="mb-0 flex-grow-1 text-center">
                {getMesLabel(formData.mes)}/{formData.ano}
              </h6>
              <Badge bg="light" text="dark">
                {diasPresentes} dia{diasPresentes !== 1 ? 's' : ''} de presença
              </Badge>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table bordered className="mb-0" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="table-light">
                  <th style={{ width: '120px', textAlign: 'center', fontWeight: 'bold' }}>Semana</th>
                  {diasDaSemana.map((dia) => (
                    <th
                      key={dia}
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {dia}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calendario.map((semana, semanaIndex) => {
                  const diasUteisSemana = contarDiasUteisSemana(semana);
                  const obrigatorios = getPresencaMinimaObrigatoria(diasUteisSemana);
                  // Contar presenças apenas nos dias do mês do relatório (mesma base dos dias úteis)
                  const presencasNaSemana = semana.filter(dia => {
                    if (dia === null || !dia.isMesAtual) return false;
                    const status = getStatusDia(dia.dia, dia.mes, dia.ano);
                    return status?.status === 'presente';
                  }).length;
                  const concluido = obrigatorios > 0 && presencasNaSemana >= obrigatorios;
                  const semanaEncerrada = semanaJaPassou(semana);
                  const emFalta = semanaEncerrada && obrigatorios > 0 && !concluido;

                  return (
                    <tr key={semanaIndex}>
                      <td
                        style={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                          verticalAlign: 'middle',
                          fontSize: '0.85rem',
                          ...(emFalta ? { backgroundColor: '#dc3545', color: '#fff' } : {}),
                        }}
                      >
                        <div>{getNomeSemana(semanaIndex)}</div>
                        {obrigatorios > 0 && (
                          <small className="d-block mt-1" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                            {presencasNaSemana}/{obrigatorios} dia(s) obrigatório(s)
                          </small>
                        )}
                      </td>
                      {semana.map((dia, diaIndex) => {
                        const isDiaUtilAtual = dia !== null && isDiaUtil(dia.dia, dia.mes, dia.ano);
                        const isFuturo = dia !== null && isDiaFuturo(dia.dia, dia.mes, dia.ano);
                        const statusDia = dia !== null ? getStatusDia(dia.dia, dia.mes, dia.ano) : undefined;
                        const isMesAtual = dia !== null ? dia.isMesAtual : false;
                        const impedimentoDia = dia !== null ? getImpedimentoDia(dia.dia, dia.mes, dia.ano) : null;
                        const temImpedimento = impedimentoDia !== null;

                        // Verificar se o dia é feriado/bloqueio (para destacar em amarelo)
                        const feriadosNaData = dia !== null
                          ? getFeriadosNaData(new Date(dia.ano, parseInt(dia.mes) - 1, dia.dia))
                          : [];
                        const temFeriado = feriadosNaData.length > 0;

                        const valorSelectAtual = temImpedimento
                          ? 'impedimento'
                          : (statusDia?.status || 'nao_marcado');

                        return (
                          <td
                            key={diaIndex}
                            ref={(el) => { if (dia !== null) justificativaButtonRefs.current[dia.dia] = el; }}
                            style={{
                              textAlign: 'center',
                              padding: '8px 4px',
                              verticalAlign: 'middle',
                              opacity: !isMesAtual ? 0.5 : 1,
                              backgroundColor:
                                emFalta && dia !== null && diaIndex !== 0 && diaIndex !== 6
                                  ? '#dc3545'
                                  : (temFeriado ? '#fff3cd' : undefined),
                              color:
                                emFalta && dia !== null && diaIndex !== 0 && diaIndex !== 6
                                  ? '#fff'
                                  : undefined,
                            }}
                            title={temFeriado ? feriadosNaData.map(f => f.nome).join(', ') : undefined}
                          >
                            {dia !== null && (
                              <div className="d-flex flex-column align-items-center">
                                <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{dia.dia}</span>

                                {/* Select para dias úteis não futuros - apenas do mês atual */}
                                {/* Mostrar select apenas para dias do período do relatório atual */}
                                {isDiaUtilAtual && !isFuturo && isMesAtual && (
                                  <Form.Select
                                    size="sm"
                                    value={valorSelectAtual}
                                    onChange={(e) => {
                                      const valor = e.target.value as 'presente' | 'ausente' | 'remoto' | 'impedimento' | 'nao_marcado';
                                      if (valor === 'ausente') {
                                        abrirJustificativaModal(dia.dia, dia.mes, dia.ano);
                                      } else if (valor === 'impedimento') {
                                        // Se tem impedimento cadastrado, usar ele
                                        if (impedimentoDia) {
                                          atualizarStatusDia(
                                            dia.dia,
                                            'impedimento',
                                            undefined,
                                            null,
                                            impedimentoDia.tipo,
                                            getTipoPeriodoNome(impedimentoDia.tipo),
                                            dia.mes,
                                            dia.ano
                                          );
                                        }
                                      } else {
                                        atualizarStatusDia(dia.dia, valor, undefined, undefined, undefined, undefined, dia.mes, dia.ano);
                                      }
                                    }}
                                    disabled={temImpedimento}
                                    style={{
                                      fontSize: '0.65rem',
                                      padding: '2px 4px',
                                      marginTop: '4px',
                                      width: '95%',
                                      borderRadius: '4px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      backgroundColor:
                                        valorSelectAtual === 'presente'
                                          ? '#198754'
                                          : valorSelectAtual === 'ausente'
                                            ? '#ffc107'
                                            : valorSelectAtual === 'remoto'
                                              ? '#0d6efd'
                                              : undefined,
                                      color:
                                        valorSelectAtual === 'presente' || valorSelectAtual === 'ausente' || valorSelectAtual === 'remoto'
                                          ? '#fff'
                                          : undefined,
                                    }}
                                  >
                                    <option value="nao_marcado">--</option>
                                    <option value="presente">Presente</option>
                                    <option value="ausente">Ausente</option>
                                    <option value="remoto">Remoto</option>
                                    {temImpedimento && (
                                      <option value="impedimento">
                                        {getTipoPeriodoNome(impedimentoDia.tipo)}
                                      </option>
                                    )}
                                  </Form.Select>
                                )}
                                {/* Indicação visual de feriado/bloqueio */}
                                {temFeriado && (
                                  <small
                                    style={{
                                      fontSize: '0.65rem',
                                      marginTop: '4px',
                                      color: '#856404',
                                      fontWeight: 600,
                                    }}
                                  >
                                    Feriado
                                  </small>
                                )}
                                {/* Mostrar apenas visualização para dias de outros períodos */}
                                {isDiaUtilAtual && !isFuturo && !isMesAtual && (
                                  <div
                                    className="text-muted"
                                    style={{
                                      fontSize: '0.65rem',
                                      marginTop: '4px',
                                      fontStyle: 'italic',
                                      cursor: 'not-allowed'
                                    }}
                                    title={`Este dia pertence ao período de ${getMesLabel(dia.mes)}/${dia.ano} e não pode ser editado no relatório de ${getMesLabel(formData.mes)}/${formData.ano}`}
                                  >
                                    {statusDia?.status === 'presente' && '✓ Presente'}
                                    {statusDia?.status === 'ausente' && '✗ Ausente'}
                                    {statusDia?.status === 'remoto' && '◐ Remoto'}
                                    {statusDia?.status === 'impedimento' && '⊘ Impedimento'}
                                    {!statusDia && '--'}
                                  </div>
                                )}

                                {/* Indicador de impedimento */}
                                {temImpedimento && isDiaUtilAtual && !isFuturo && (
                                  <small style={{ fontSize: '0.55rem', marginTop: '2px' }} title={impedimentoDia.motivo || ''}>
                                    <i className="bi bi-calendar-x me-1"></i>
                                    {getTipoPeriodoNome(impedimentoDia.tipo).substring(0, 8)}...
                                  </small>
                                )}

                                {/* Ícone de justificativa - apenas para dias do período atual */}
                                {statusDia?.status === 'ausente' && (statusDia.justificativaTexto || statusDia.justificativaArquivo) && isMesAtual && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 mt-1"
                                    onClick={() => abrirJustificativaModal(dia.dia, dia.mes, dia.ano)}
                                    title="Ver/Editar justificativa"
                                  >
                                    <i className="bi bi-file-text"></i>
                                  </Button>
                                )}

                                {/* Indicador de dia futuro */}
                                {isFuturo && (
                                  <small style={{ fontSize: '0.6rem' }}>
                                    <i className="bi bi-lock"></i>
                                  </small>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer className="bg-light">
            <Row>
              <Col md={4} className="text-end">
                <Badge className="me-2" style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                  <i className="bi bi-check-circle me-1"></i>
                  {diasPresentes} Presença{diasPresentes !== 1 ? 's' : ''}
                </Badge>
              </Col>
            </Row>
          </Card.Footer>
        </Card>

        {/* Modal de Justificativa */}
        <Overlay
          show={justificativaModalDia !== null}
          target={justificativaModalDia !== null ? justificativaButtonRefs.current[justificativaModalDia] : null}
          placement="auto"
          containerPadding={20}
          rootClose
          rootCloseEvent="click"
          onHide={cancelarJustificativa}
        >
          <Popover id="popover-justificativa" style={{ maxWidth: '400px' }}>
            <Popover.Header as="h3" className="bg-warning text-dark">
              <i className="bi bi-file-earmark-text me-2"></i>
              Justificativa de Ausência - Dia {justificativaModalDia}
            </Popover.Header>
            <Popover.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Texto da Justificativa</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Descreva o motivo da ausência..."
                  value={justificativaTextoTemp}
                  onChange={(e) => setJustificativaTextoTemp(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Arquivo de Comprovação</Form.Label>
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ borderRadius: '8px' }}
                />
                {justificativaArquivoTemp && (
                  <small className="text-muted d-block mt-1">
                    <i className="bi bi-paperclip me-1"></i>
                    {justificativaArquivoTemp.name}
                  </small>
                )}
              </Form.Group>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={cancelarJustificativa}
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-x-lg me-1"></i>Cancelar
                </Button>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={salvarJustificativa}
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-check-lg me-1"></i>Salvar Ausência
                </Button>
              </div>
            </Popover.Body>
          </Popover>
        </Overlay>
      </Fragment>
    );
  };

  const renderStep2 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={5}>
          <Form.Group>
            <Form.Label className="fw-bold">Autor</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome do autor"
              value={novoAutor}
              onChange={(e) => setNovoAutor(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarAutorNatureza();
                }
              }}
              style={{ borderRadius: '8px' }}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold">Natureza da Ação</Form.Label>
            <Select
              classNamePrefix="react-select"
              value={naturezaAcaoOptions.find(o => o.value === novaNatureza) || null}
              onChange={(option) => setNovaNatureza(option ? (option as NaturezaAcaoOption).value : '')}
              options={naturezaAcaoOptions}
              placeholder="Digite para buscar..."
              isClearable
              isDisabled={isViewMode}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            variant="primary"
            onClick={adicionarAutorNatureza}
            className="w-100"
            style={{ borderRadius: '8px' }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Adicionar
          </Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Adicionar</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhasAutor(prev => Math.max(1, prev - 1))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-dash">-</i>
            </Button>
            <Form.Control
              type="number"
              min="1"
              max="50"
              value={quantidadeLinhasAutor}
              onChange={(e) => setQuantidadeLinhasAutor(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
              size="sm"
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhasAutor(prev => Math.min(50, prev + 1))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-plus">+</i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={adicionarMultiplasLinhasAutor}
              style={{ borderRadius: '8px' }}
            >
              <i className="bi bi-plus-lg me-1"></i>
              Adicionar {quantidadeLinhasAutor} linha{quantidadeLinhasAutor > 1 ? 's' : ''}
            </Button>
          </div>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end">
          <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}><small className="text-muted">Total de Autores:</small> {formData.autorNatureza.itens.length}</h5>
        </Col>
      </Row>

      {formData.autorNatureza.itens.length === 0 ? (
        <div className="text-center text-muted py-4 border rounded">
          <i className="bi bi-inbox fs-1"></i>
          <p className="mb-0 mt-2">Nenhum autor adicionado ainda.</p>
          <small>Adicione autores e natureza das ações acima.</small>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th>Autor</th>
              <th style={{ width: '200px' }}>Natureza da Ação</th>
              <th style={{ width: '200px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {formData.autorNatureza.itens.map((item, index) => {
              const estaEditando = editandoAutorNatureza === item.id;
              const estaConfirmandoExclusao = confirmandoExclusaoAutor === item.id;
              const refKey = `autor-${item.id}`;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={valorEdicaoAutor}
                        onChange={(e) => setValorEdicaoAutor(e.target.value)}
                        style={{ borderRadius: '8px' }}
                        autoFocus
                      />
                    ) : item.autor ? (
                      <span>{item.autor}</span>
                    ) : (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Digite o nome do autor"
                        style={{ borderRadius: '8px' }}
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            setFormData(prev => ({
                              ...prev,
                              autorNatureza: {
                                itens: prev.autorNatureza.itens.map(i =>
                                  i.id === item.id ? { ...i, autor: e.target.value.trim() } : i
                                ),
                              },
                            }));
                          }
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Select
                        size="sm"
                        value={valorEdicaoNatureza}
                        onChange={(e) => setValorEdicaoNatureza(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      >
                        {naturezaAcaoOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : item.naturezaAcao ? (
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {naturezaAcaoOptions.find(o => o.value === item.naturezaAcao)?.label || item.naturezaAcao}
                      </Badge>
                    ) : (
                      <Form.Select
                        size="sm"
                        style={{ borderRadius: '8px' }}
                        onChange={(e) => {
                          if (e.target.value) {
                            setFormData(prev => ({
                              ...prev,
                              autorNatureza: {
                                itens: prev.autorNatureza.itens.map(i =>
                                  i.id === item.id ? { ...i, naturezaAcao: e.target.value } : i
                                ),
                              },
                            }));
                          }
                        }}
                      >
                        {naturezaAcaoOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px', backgroundColor: RELATORIO_CORES.PRIMARIA, borderColor: RELATORIO_CORES.PRIMARIA }}
                          variant="success"
                          size="sm"
                          onClick={() => salvarEdicaoAutorNatureza(item.id)}
                          title="Salvar"
                        >
                          <i className="bi bi-check-lg"> Salvar</i>
                        </Button>
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="secondary"
                          size="sm"
                          onClick={cancelarEdicaoAutorNatureza}
                          title="Cancelar"
                        >
                          <i className="bi bi-x-lg"> Cancelar</i>
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px', borderColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.PRIMARIA }}
                          variant="outline-primary"
                          size="sm"
                          onClick={() => iniciarEdicaoAutorNatureza(item)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"> Editar</i>
                        </Button>
                        <Button
                          ref={(el) => { excluirAutorButtonRefs.current[refKey] = el; }}
                          style={{ width: '80px', height: '40px' }}
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setConfirmandoExclusaoAutor(item.id)}
                          title="Excluir"
                        >
                          <i className="bi bi-trash"> Excluir</i>
                        </Button>
                        <Overlay
                          show={estaConfirmandoExclusao}
                          target={excluirAutorButtonRefs.current[refKey]}
                          placement="top"
                          containerPadding={20}
                        >
                          <Popover id={`popover-delete-autor-${refKey}`}>
                            <Popover.Header as="h3" className="text-center">
                              Deseja mesmo excluir?
                            </Popover.Header>
                            <Popover.Body className="d-flex gap-2 justify-content-center">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => confirmarExclusaoAutor(item.id)}
                              >
                                Sim
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setConfirmandoExclusaoAutor(null)}
                              >
                                Não
                              </Button>
                            </Popover.Body>
                          </Popover>
                        </Overlay>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Fragment>
  );

  const renderStep3 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.° do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoContestacao}
              onChange={setNovoProcessoContestacao}
              onAdicionar={() => adicionarProcesso('contestacao', novoProcessoContestacao, setNovoProcessoContestacao)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <ControlesLinha
        quantidade={quantidadeLinhas.contestacao}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, contestacao: Math.max(1, prev.contestacao - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, contestacao: Math.min(50, prev.contestacao + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, contestacao: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('contestacao')}
        totalLabel="Total de Processos"
        totalCount={formData.contestacao.processos.length}
      />

      {renderTabelaProcessos('contestacao', formData.contestacao.processos)}
    </Fragment>
  );

  const renderStep4 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoReconvencao}
              onChange={setNovoProcessoReconvencao}
              onAdicionar={() => adicionarProcesso('reconvencao', novoProcessoReconvencao, setNovoProcessoReconvencao)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <ControlesLinha
        quantidade={quantidadeLinhas.reconvencao}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, reconvencao: Math.max(1, prev.reconvencao - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, reconvencao: Math.min(50, prev.reconvencao + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, reconvencao: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('reconvencao')}
        totalLabel="Total de Processos"
        totalCount={formData.reconvencao.processos.length}
      />

      {renderTabelaProcessos('reconvencao', formData.reconvencao.processos)}
    </Fragment>
  );

  const renderStep5 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoReplica}
              onChange={setNovoProcessoReplica}
              onAdicionar={() => adicionarProcesso('replica', novoProcessoReplica, setNovoProcessoReplica)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <ControlesLinha
        quantidade={quantidadeLinhas.replica}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, replica: Math.max(1, prev.replica - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, replica: Math.min(50, prev.replica + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, replica: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('replica')}
        totalLabel="Total de Processos"
        totalCount={formData.replica.processos.length}
      />

      {renderTabelaProcessos('replica', formData.replica.processos)}
    </Fragment>
  );

  const renderStep6 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoManifestacao}
              onChange={setNovoProcessoManifestacao}
              onAdicionar={() => adicionarProcesso('manifestacao', novoProcessoManifestacao, setNovoProcessoManifestacao)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <ControlesLinha
        quantidade={quantidadeLinhas.manifestacao}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, manifestacao: Math.max(1, prev.manifestacao - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, manifestacao: Math.min(50, prev.manifestacao + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, manifestacao: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('manifestacao')}
        totalLabel="Total de Processos"
        totalCount={formData.manifestacao.processos.length}
      />

      {renderTabelaProcessos('manifestacao', formData.manifestacao.processos)}
    </Fragment>
  );

  const renderStep7 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoRazoesFinais}
              onChange={setNovoProcessoRazoesFinais}
              onAdicionar={() => adicionarProcesso('razoesFinais', novoProcessoRazoesFinais, setNovoProcessoRazoesFinais)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <ControlesLinha
        quantidade={quantidadeLinhas.razoesFinais}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, razoesFinais: Math.max(1, prev.razoesFinais - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, razoesFinais: Math.min(50, prev.razoesFinais + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, razoesFinais: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('razoesFinais')}
        totalLabel="Total de Processos"
        totalCount={formData.razoesFinais.processos.length}
      />

      {renderTabelaProcessos('razoesFinais', formData.razoesFinais.processos)}
    </Fragment>
  );

  const renderStep8 = () => {

    return (
      <Fragment>
        {/* Sub-etapa 1: Audiências Presenciais */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-person-video me-2"></i>
              Audiências Presenciais
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Audiência Presencial</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={novaAudienciaPresencial ? { value: novaAudienciaPresencial, label: novaAudienciaPresencial } : null}
                    onChange={(option) => setNovaAudienciaPresencial(option ? (option as { value: string; label: string }).value : '')}
                    options={TIPOS_AUDIENCIAS.map((tipo) => ({ value: tipo, label: tipo }))}
                    placeholder="Digite para buscar..."
                    isClearable
                    isDisabled={isViewMode}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: 0000000"
                    value={novoNumeroProtocolo}
                    onChange={(e) => setNovoNumeroProtocolo(apenasNumeros(e.target.value))}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">Data</Form.Label>
                  <Form.Control
                    type="date"
                    value={novaDataPresencial}
                    onChange={(e) => setNovaDataPresencial(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={adicionarAudienciaPresencial}
                  className="w-100"
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Adicionar
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, audienciasPresenciais: Math.max(1, prev.audienciasPresenciais - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    title="Diminuir quantidade"
                  >
                    <i className="bi bi-dash" aria-hidden="true"></i> -
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.audienciasPresenciais}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, audienciasPresenciais: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, audienciasPresenciais: Math.min(50, prev.audienciasPresenciais + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    title="Aumentar quantidade"
                  >
                    <i className="bi bi-plus" aria-hidden="true"></i> +
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasAudienciasPresenciais}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.audienciasPresenciais} linha{quantidadeLinhas.audienciasPresenciais > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '30%' }}>Audiência Presencial</th>
                  <th style={{ width: '25%' }}>N.º do Processo</th>
                  <th style={{ width: '20%' }}>Data</th>
                  <th style={{ width: '20%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {formData.audiencias.presenciais.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Nenhuma audiência presencial adicionada
                    </td>
                  </tr>
                ) : (
                  formData.audiencias.presenciais.map((item, index) => {
                    const estaEditando = editandoAudienciaPresencial === item.id;

                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          {estaEditando ? (
                            <Select
                              classNamePrefix="react-select"
                              value={valorEdicaoAudienciaPresencial ? { value: valorEdicaoAudienciaPresencial, label: valorEdicaoAudienciaPresencial } : null}
                              onChange={(option) => setValorEdicaoAudienciaPresencial(option ? (option as { value: string; label: string }).value : '')}
                              options={TIPOS_AUDIENCIAS.map((tipo) => ({ value: tipo, label: tipo }))}
                              placeholder="Digite para buscar..."
                              isClearable
                              isDisabled={isViewMode}
                              styles={{ container: (base) => ({ ...base, minWidth: '180px' }) }}
                            />
                          ) : (
                            item.audienciaPresencial
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Ex: 0000000"
                              value={valorEdicaoNumeroProtocolo}
                              onChange={(e) => setValorEdicaoNumeroProtocolo(apenasNumeros(e.target.value))}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                  e.preventDefault();
                                }
                              }}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            item.numeroProtocolo
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="date"
                              size="sm"
                              value={valorEdicaoDataPresencial}
                              onChange={(e) => setValorEdicaoDataPresencial(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            formatarDataHelper(item.data)
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="success"
                                size="sm"
                                onClick={() => salvarEdicaoAudienciaPresencial(item.id)}
                                title="Salvar"
                              >
                                <i className="bi bi-check-lg"> Salvar</i>
                              </Button>
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="secondary"
                                size="sm"
                                onClick={cancelarEdicaoAudienciaPresencial}
                                title="Cancelar"
                              >
                                <i className="bi bi-x-lg"> Cancelar</i>
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="outline-primary"
                                size="sm"
                                onClick={() => iniciarEdicaoAudienciaPresencial(item)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"> Editar</i>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removerAudienciaPresencial(item.id)}
                                style={{ width: '80px', height: '40px' }}
                                title="Excluir"
                              >
                                <i className="bi bi-trash"> Excluir</i>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total:</small> {formData.audiencias.presenciais.length}
              </h5>
            </div>
          </Card.Body>
        </Card>

        {/* Sub-etapa 2: Audiências Remotas */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-camera-video me-2"></i>
              Audiências Remotas
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Audiência Remota</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={novaAudienciaRemota ? { value: novaAudienciaRemota, label: novaAudienciaRemota } : null}
                    onChange={(option) => setNovaAudienciaRemota(option ? (option as { value: string; label: string }).value : '')}
                    options={TIPOS_AUDIENCIAS.map((tipo) => ({ value: tipo, label: tipo }))}
                    placeholder="Digite para buscar..."
                    isClearable
                    isDisabled={isViewMode}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: 000000.000.000"
                    value={novoNumeroProcessoRemota}
                    onChange={(e) => setNovoNumeroProcessoRemota(apenasNumeros(e.target.value))}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">Data</Form.Label>
                  <Form.Control
                    type="date"
                    value={novaDataRemota}
                    onChange={(e) => setNovaDataRemota(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={adicionarAudienciaRemota}
                  className="w-100"
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Adicionar
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, audienciasRemotas: Math.max(1, prev.audienciasRemotas - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    title="Diminuir quantidade"
                  >
                    <i className="bi bi-dash" aria-hidden="true"></i> -
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.audienciasRemotas}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, audienciasRemotas: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, audienciasRemotas: Math.min(50, prev.audienciasRemotas + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                    title="Aumentar quantidade"
                  >
                    <i className="bi bi-plus" aria-hidden="true"></i> +
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasAudienciasRemotas}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.audienciasRemotas} linha{quantidadeLinhas.audienciasRemotas > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '35%' }}>Audiência Remota</th>
                  <th style={{ width: '25%' }}>N.º do Processo</th>
                  <th style={{ width: '20%' }}>Data</th>
                  <th style={{ width: '15%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {formData.audiencias.remotas.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Nenhuma audiência remota adicionada
                    </td>
                  </tr>
                ) : (
                  formData.audiencias.remotas.map((item, index) => {
                    const estaEditando = editandoAudienciaRemota === item.id;

                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          {estaEditando ? (
                            <Select
                              classNamePrefix="react-select"
                              value={valorEdicaoAudienciaRemota ? { value: valorEdicaoAudienciaRemota, label: valorEdicaoAudienciaRemota } : null}
                              onChange={(option) => setValorEdicaoAudienciaRemota(option ? (option as { value: string; label: string }).value : '')}
                              options={TIPOS_AUDIENCIAS.map((tipo) => ({ value: tipo, label: tipo }))}
                              placeholder="Digite para buscar..."
                              isClearable
                              isDisabled={isViewMode}
                              styles={{ container: (base) => ({ ...base, minWidth: '180px' }) }}
                            />
                          ) : (
                            item.audienciaRemota
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Ex: 000000.000.000"
                              value={valorEdicaoNumeroProcessoRemota}
                              onChange={(e) => setValorEdicaoNumeroProcessoRemota(apenasNumeros(e.target.value))}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                  e.preventDefault();
                                }
                              }}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            item.numeroProcesso
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="date"
                              size="sm"
                              value={valorEdicaoDataRemota}
                              onChange={(e) => setValorEdicaoDataRemota(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            formatarDataHelper(item.data)
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="success"
                                size="sm"
                                onClick={() => salvarEdicaoAudienciaRemota(item.id)}
                                title="Salvar"
                              >
                                <i className="bi bi-check-lg"> Salvar</i>
                              </Button>
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="secondary"
                                size="sm"
                                onClick={cancelarEdicaoAudienciaRemota}
                                title="Cancelar"
                              >
                                <i className="bi bi-x-lg"> Cancelar</i>
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="outline-primary"
                                size="sm"
                                onClick={() => iniciarEdicaoAudienciaRemota(item)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"> Editar</i>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removerAudienciaRemota(item.id)}
                                style={{ width: '80px', height: '40px' }}
                                title="Excluir"
                              >
                                <i className="bi bi-trash"> Excluir</i>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total:</small> {formData.audiencias.remotas.length}
              </h5>
            </div>
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  const renderStep9 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoRecursos}
              onChange={setNovoProcessoRecursos}
              onAdicionar={() => adicionarProcesso('recursos', novoProcessoRecursos, setNovoProcessoRecursos)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <ControlesLinha
        quantidade={quantidadeLinhas.recursos}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, recursos: Math.max(1, prev.recursos - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, recursos: Math.min(50, prev.recursos + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, recursos: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('recursos')}
        totalLabel="Total de Processos"
        totalCount={formData.recursos.processos.length}
      />
      {renderTabelaProcessos('recursos', formData.recursos.processos)}
    </Fragment>
  );

  const renderStep10 = () => {

    return (
      <Fragment>
        {/* Sub-etapa 1: Atendimentos Presenciais */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-person-check me-2"></i>
              Atendimentos Presenciais
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-bold">Arquivo</Form.Label>
                  <Form.Control
                    type="file"
                    ref={arquivoAtendimentoPresencialInputRef}
                    onChange={handleFileChangeAtendimentoPresencial}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Form.Text className="text-muted">Arquivo de até 10MB</Form.Text>
                  {novoArquivoAtendimentoPresencial && (
                    <div className="mt-2">
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {novoArquivoAtendimentoPresencial.name} ({(novoArquivoAtendimentoPresencial.size / 1024 / 1024).toFixed(2)} MB)
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-bold">Observação</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Observações (opcional)"
                    value={novaObservacaoAtendimentoPresencial}
                    onChange={(e) => setNovaObservacaoAtendimentoPresencial(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={adicionarAtendimentoPresencial}
                  className="w-100"
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Adicionar
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, atendimentosPresenciais: Math.max(1, prev.atendimentosPresenciais - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.atendimentosPresenciais}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, atendimentosPresenciais: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, atendimentosPresenciais: Math.min(50, prev.atendimentosPresenciais + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasAtendimentosPresenciais}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.atendimentosPresenciais} linha{quantidadeLinhas.atendimentosPresenciais > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '40%' }}>Arquivo</th>
                  <th style={{ width: '40%' }}>Observação</th>
                  <th style={{ width: '15%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {formData.atendimento.presenciais.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      Nenhum atendimento presencial adicionado
                    </td>
                  </tr>
                ) : (
                  formData.atendimento.presenciais.map((item, index) => {
                    const estaEditando = editandoAtendimentoPresencial === item.id;

                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          {estaEditando ? (
                            <div>
                              <Form.Control
                                type="file"
                                size="sm"
                                ref={arquivoAtendimentoPresencialEdicaoInputRef}
                                onChange={handleFileChangeAtendimentoPresencialEdicao}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                style={{ borderRadius: '8px' }}
                              />
                              {valorEdicaoArquivoAtendimentoPresencial && (
                                <small className="text-muted d-block mt-1">
                                  {valorEdicaoArquivoAtendimentoPresencial.name} ({(valorEdicaoArquivoAtendimentoPresencial.size / 1024 / 1024).toFixed(2)} MB)
                                </small>
                              )}
                              {!valorEdicaoArquivoAtendimentoPresencial && item.arquivoNome && (
                                <small className="text-muted d-block mt-1">
                                  Arquivo atual: {item.arquivoNome}
                                </small>
                              )}
                            </div>
                          ) : (
                            item.arquivoNome ? (
                              <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-file-earmark"></i>
                                <span>{item.arquivoNome}</span>
                                {item.arquivo && (
                                  <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                                    {(item.arquivo.size / 1024 / 1024).toFixed(2)} MB
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">Sem arquivo</span>
                            )
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Observações (opcional)"
                              value={valorEdicaoObservacaoAtendimentoPresencial}
                              onChange={(e) => setValorEdicaoObservacaoAtendimentoPresencial(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            item.observacao || '-'
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="success"
                                size="sm"
                                onClick={() => salvarEdicaoAtendimentoPresencial(item.id)}
                                title="Salvar"
                              >
                                <i className="bi bi-check-lg"> Salvar</i>
                              </Button>
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="secondary"
                                size="sm"
                                onClick={cancelarEdicaoAtendimentoPresencial}
                                title="Cancelar"
                              >
                                <i className="bi bi-x-lg"> Cancelar</i>
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="outline-primary"
                                size="sm"
                                onClick={() => iniciarEdicaoAtendimentoPresencial(item)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"> Editar</i>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removerAtendimentoPresencial(item.id)}
                                style={{ width: '80px', height: '40px' }}
                                title="Excluir"
                              >
                                <i className="bi bi-trash"> Excluir</i>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total:</small> {formData.atendimento.presenciais.length}
              </h5>
            </div>
          </Card.Body>
        </Card>

        {/* Sub-etapa 2: Atendimentos Remotos */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-telephone me-2"></i>
              Atendimentos Remotos
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-bold">Arquivo</Form.Label>
                  <Form.Control
                    type="file"
                    ref={arquivoAtendimentoRemotoInputRef}
                    onChange={handleFileChangeAtendimentoRemoto}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Form.Text className="text-muted">Arquivo de até 10MB</Form.Text>
                  {novoArquivoAtendimentoRemoto && (
                    <div className="mt-2">
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {novoArquivoAtendimentoRemoto.name} ({(novoArquivoAtendimentoRemoto.size / 1024 / 1024).toFixed(2)} MB)
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-bold">Observação</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Observações (opcional)"
                    value={novaObservacaoAtendimentoRemoto}
                    onChange={(e) => setNovaObservacaoAtendimentoRemoto(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={adicionarAtendimentoRemoto}
                  className="w-100"
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Adicionar
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, atendimentosRemotos: Math.max(1, prev.atendimentosRemotos - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.atendimentosRemotos}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, atendimentosRemotos: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, atendimentosRemotos: Math.min(50, prev.atendimentosRemotos + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasAtendimentosRemotos}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.atendimentosRemotos} linha{quantidadeLinhas.atendimentosRemotos > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '40%' }}>Arquivo</th>
                  <th style={{ width: '40%' }}>Observação</th>
                  <th style={{ width: '15%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {formData.atendimento.remotas.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      Nenhum atendimento remoto adicionado
                    </td>
                  </tr>
                ) : (
                  formData.atendimento.remotas.map((item, index) => {
                    const estaEditando = editandoAtendimentoRemoto === item.id;

                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          {estaEditando ? (
                            <div>
                              <Form.Control
                                type="file"
                                size="sm"
                                ref={arquivoAtendimentoRemotoEdicaoInputRef}
                                onChange={handleFileChangeAtendimentoRemotoEdicao}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                style={{ borderRadius: '8px' }}
                              />
                              {valorEdicaoArquivoAtendimentoRemoto && (
                                <small className="text-muted d-block mt-1">
                                  {valorEdicaoArquivoAtendimentoRemoto.name} ({(valorEdicaoArquivoAtendimentoRemoto.size / 1024 / 1024).toFixed(2)} MB)
                                </small>
                              )}
                              {!valorEdicaoArquivoAtendimentoRemoto && item.arquivoNome && (
                                <small className="text-muted d-block mt-1">
                                  Arquivo atual: {item.arquivoNome}
                                </small>
                              )}
                            </div>
                          ) : (
                            item.arquivoNome ? (
                              <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-file-earmark"></i>
                                <span>{item.arquivoNome}</span>
                                {item.arquivo && (
                                  <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                                    {(item.arquivo.size / 1024 / 1024).toFixed(2)} MB
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">Sem arquivo</span>
                            )
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Observações (opcional)"
                              value={valorEdicaoObservacaoAtendimentoRemoto}
                              onChange={(e) => setValorEdicaoObservacaoAtendimentoRemoto(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          ) : (
                            item.observacao || '-'
                          )}
                        </td>
                        <td>
                          {estaEditando ? (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="success"
                                size="sm"
                                onClick={() => salvarEdicaoAtendimentoRemoto(item.id)}
                                title="Salvar"
                              >
                                <i className="bi bi-check-lg"> Salvar</i>
                              </Button>
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="secondary"
                                size="sm"
                                onClick={cancelarEdicaoAtendimentoRemoto}
                                title="Cancelar"
                              >
                                <i className="bi bi-x-lg"> Cancelar</i>
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1">
                              <Button
                                style={{ width: '80px', height: '40px' }}
                                variant="outline-primary"
                                size="sm"
                                onClick={() => iniciarEdicaoAtendimentoRemoto(item)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"> Editar</i>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removerAtendimentoRemoto(item.id)}
                                style={{ width: '80px', height: '40px' }}
                                title="Excluir"
                              >
                                <i className="bi bi-trash"> Excluir</i>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total:</small> {formData.atendimento.remotas.length}
              </h5>
            </div>
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  const handleFileChangeAcordo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.warning('O arquivo deve ter no máximo 10MB.');
        if (anexoAcordoInputRef.current) {
          anexoAcordoInputRef.current.value = '';
        }
        return;
      }
      setNovoAnexoAcordo(file);
    }
  };

  const renderStep11 = () => {
    return (
      <Fragment>
        {/* Linha 1: legendas */}
        <Row className="mb-1">
          <Col md={6}>
            <Form.Label className="fw-bold mb-0">Nome da Parte</Form.Label>
          </Col>
          <Col md={5}>
            <Form.Label className="fw-bold mb-0">Anexo</Form.Label>
          </Col>
          <Col md={1} />
        </Row>
        {/* Linha 2: inputs e botão Adicionar */}
        <Row className="mb-1 align-items-center">
          <Col md={6}>
            <Form.Control
              type="text"
              placeholder="Ex: João Silva"
              value={novoNomeParteAcordo}
              onChange={(e) => setNovoNomeParteAcordo(apenasLetras(e.target.value))}
              onKeyPress={(e) => {
                if (!/[a-zA-ZÀ-ÿ\s]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
            />
          </Col>
          <Col md={5}>
            <Form.Control
              ref={anexoAcordoInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChangeAcordo}
            />
          </Col>
          <Col md={1}>
            <Button
              variant="primary"
              onClick={adicionarAcordoExtrajudicial}
              style={{ borderRadius: '8px' }}
              className="w-100"
            >
              <i className="bi bi-plus-lg me-2"></i>
              Adicionar
            </Button>
          </Col>
        </Row>
        {/* Linha 3: anotação do anexo */}
        <Row className="mb-3">
          <Col md={6} />
          <Col md={5}>
            <Form.Text className="text-muted">Arquivo de até 10MB (PDF, DOC, DOCX, JPG, JPEG, PNG)</Form.Text>
          </Col>
          <Col md={1} />
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted">Adicionar</span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setQuantidadeLinhas(prev => ({ ...prev, acordosExtrajudiciais: Math.max(1, prev.acordosExtrajudiciais - 1) }))}
                style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
              >
                <i className="bi bi-dash">-</i>
              </Button>
              <Form.Control
                type="number"
                min="1"
                max="50"
                value={quantidadeLinhas.acordosExtrajudiciais}
                onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, acordosExtrajudiciais: Math.max(1, parseInt(e.target.value) || 1) }))}
                style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                size="sm"
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setQuantidadeLinhas(prev => ({ ...prev, acordosExtrajudiciais: Math.min(50, prev.acordosExtrajudiciais + 1) }))}
                style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
              >
                <i className="bi bi-plus">+</i>
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={adicionarMultiplasLinhasAcordosExtrajudiciais}
                style={{ borderRadius: '8px' }}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Adicionar {quantidadeLinhas.acordosExtrajudiciais} linha{quantidadeLinhas.acordosExtrajudiciais > 1 ? 's' : ''}
              </Button>
            </div>
          </Col>
          <Col md={6} className="d-flex align-items-center justify-content-end">
            <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
              <small className="text-muted">Total de Itens:</small> {formData.acordosExtrajudiciais.itens.length}
            </h5>
          </Col>
        </Row>

        <Table bordered hover>
          <thead className="table-light">
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '35%' }}>Nome da Parte</th>
              <th style={{ width: '35%' }}>Anexo</th>
              <th style={{ width: '25%' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {formData.acordosExtrajudiciais.itens.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  Nenhum acordo extrajudicial adicionado ainda
                </td>
              </tr>
            ) : (
              formData.acordosExtrajudiciais.itens.map((item, index) => {
                const estaEditando = editandoAcordoExtrajudicial === item.id;

                return (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>
                      {estaEditando ? (
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Ex: João Silva"
                          value={valorEdicaoNomeParteAcordo}
                          onChange={(e) => setValorEdicaoNomeParteAcordo(apenasLetras(e.target.value))}
                          style={{ borderRadius: '8px' }}
                        />
                      ) : (
                        item.nomeParte
                      )}
                    </td>
                    <td>
                      {estaEditando ? (
                        <div>
                          <Form.Control
                            type="file"
                            size="sm"
                            ref={anexoAcordoEdicaoInputRef}
                            onChange={handleFileChangeAcordoEdicao}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            style={{ borderRadius: '8px' }}
                          />
                          {valorEdicaoAnexoAcordo && (
                            <small className="text-muted d-block mt-1">
                              {valorEdicaoAnexoAcordo.name} ({(valorEdicaoAnexoAcordo.size / 1024 / 1024).toFixed(2)} MB)
                            </small>
                          )}
                          {!valorEdicaoAnexoAcordo && item.anexoNome && (
                            <small className="text-muted d-block mt-1">
                              Arquivo atual: {item.anexoNome}
                            </small>
                          )}
                        </div>
                      ) : (
                        item.anexoNome ? (
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-file-earmark"></i>
                            <span>{item.anexoNome}</span>
                            {item.anexo && (
                              <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                                {(item.anexo.size / 1024 / 1024).toFixed(2)} MB
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Sem anexo</span>
                        )
                      )}
                    </td>
                    <td>
                      {estaEditando ? (
                        <div className="d-flex gap-1">
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="success"
                            size="sm"
                            onClick={() => salvarEdicaoAcordoExtrajudicial(item.id)}
                            title="Salvar"
                          >
                            <i className="bi bi-check-lg"> Salvar</i>
                          </Button>
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="secondary"
                            size="sm"
                            onClick={cancelarEdicaoAcordoExtrajudicial}
                            title="Cancelar"
                          >
                            <i className="bi bi-x-lg"> Cancelar</i>
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="outline-primary"
                            size="sm"
                            onClick={() => iniciarEdicaoAcordoExtrajudicial(item)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil"> Editar</i>
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removerAcordoExtrajudicial(item.id)}
                            style={{ width: '80px', height: '40px' }}
                            title="Excluir"
                          >
                            <i className="bi bi-trash"> Excluir</i>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Fragment>
    );
  };

  // Função helper para renderizar tabela de processos simples (honorários e ofícios)
  const renderTabelaProcessosSimples = (
    processos: (HonorarioSentencaItem | HonorarioCumprimentoItem | OficioRequisitorioItem | OficioDemaisItem)[],
    onRemover: (id: string) => void,
    editandoId?: string | null,
    valorEdicao?: string,
    onValorEdicaoChange?: (value: string) => void,
    onIniciarEdicao?: (item: HonorarioSentencaItem | HonorarioCumprimentoItem | OficioRequisitorioItem | OficioDemaisItem) => void,
    onSalvarEdicao?: (id: string) => void,
    onCancelarEdicao?: () => void
  ) => {
    if (processos.length === 0) {
      return (
        <div className="text-center text-muted py-4 border rounded">
          <i className="bi bi-inbox fs-1"></i>
          <p className="mb-0 mt-2">Nenhum processo adicionado ainda.</p>
          <small>Adicione os números dos processos acima.</small>
        </div>
      );
    }

    return (
      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th style={{ width: '50px' }}>#</th>
            <th>Número do Processo</th>
            <th style={{ width: '200px' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {processos.map((processo, index) => {
            const estaEditando = editandoId === processo.id;

            return (
              <tr key={processo.id}>
                <td>{index + 1}</td>
                <td>
                  {estaEditando && valorEdicao !== undefined && onValorEdicaoChange ? (
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Ex: 000000.000.000"
                      value={valorEdicao}
                      onChange={(e) => onValorEdicaoChange(apenasNumeros(e.target.value))}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px' }}
                    />
                  ) : (
                    processo.numeroProcesso
                  )}
                </td>
                <td>
                  {estaEditando && onSalvarEdicao && onCancelarEdicao ? (
                    <div className="d-flex gap-1">
                      <Button
                        style={{ width: '80px', height: '40px' }}
                        variant="success"
                        size="sm"
                        onClick={() => onSalvarEdicao(processo.id)}
                        title="Salvar"
                      >
                        <i className="bi bi-check-lg"> Salvar</i>
                      </Button>
                      <Button
                        style={{ width: '80px', height: '40px' }}
                        variant="secondary"
                        size="sm"
                        onClick={onCancelarEdicao}
                        title="Cancelar"
                      >
                        <i className="bi bi-x-lg"> Cancelar</i>
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex gap-1">
                      {onIniciarEdicao && (
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onIniciarEdicao(processo)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"> Editar</i>
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onRemover(processo.id)}
                        style={{ width: '80px', height: '40px' }}
                        title="Excluir"
                      >
                        <i className="bi bi-trash"> Excluir</i>
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderStep12 = () => {
    return (
      <Fragment>
        {/* Sub-etapa 1: Sentença/Acórdão com fixação de honorários */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-file-earmark-text me-2"></i>
              Sentença/Acórdão com fixação de honorários advocatícios em favor da Defensoria Pública
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={10}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 000000.000.000"
                      value={novoProcessoSentencaAcordao}
                      onChange={(e) => setNovoProcessoSentencaAcordao(apenasNumeros(e.target.value))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarHonorarioSentencaAcordao();
                        }
                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px 0 0 8px' }}
                    />
                    <Button
                      variant="primary"
                      onClick={adicionarHonorarioSentencaAcordao}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Adicionar
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, honorariosSentencaAcordao: Math.max(1, prev.honorariosSentencaAcordao - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.honorariosSentencaAcordao}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, honorariosSentencaAcordao: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, honorariosSentencaAcordao: Math.min(50, prev.honorariosSentencaAcordao + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasHonorariosSentencaAcordao}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.honorariosSentencaAcordao} linha{quantidadeLinhas.honorariosSentencaAcordao > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            {renderTabelaProcessosSimples(
              formData.honorarios.sentencaAcordao,
              removerHonorarioSentencaAcordao,
              editandoHonorarioSentencaAcordao,
              valorEdicaoProcessoSentencaAcordao,
              setValorEdicaoProcessoSentencaAcordao,
              iniciarEdicaoHonorarioSentencaAcordao,
              salvarEdicaoHonorarioSentencaAcordao,
              cancelarEdicaoHonorarioSentencaAcordao
            )}

            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total de Processos:</small> {formData.honorarios.sentencaAcordao.length}
              </h5>
            </div>
          </Card.Body>
        </Card>

        {/* Sub-etapa 2: Cumprimento de Sentença de Honorários */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-file-check me-2"></i>
              Cumprimento de Sentença de Honorários ajuizados em favor da Defensoria Pública
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={10}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 000000.000.000"
                      value={novoProcessoCumprimento}
                      onChange={(e) => setNovoProcessoCumprimento(apenasNumeros(e.target.value))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarHonorarioCumprimento();
                        }
                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px 0 0 8px' }}
                    />
                    <Button
                      variant="primary"
                      onClick={adicionarHonorarioCumprimento}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Adicionar
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, honorariosCumprimento: Math.max(1, prev.honorariosCumprimento - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.honorariosCumprimento}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, honorariosCumprimento: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, honorariosCumprimento: Math.min(50, prev.honorariosCumprimento + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasHonorariosCumprimento}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.honorariosCumprimento} linha{quantidadeLinhas.honorariosCumprimento > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            {renderTabelaProcessosSimples(
              formData.honorarios.cumprimento,
              removerHonorarioCumprimento,
              editandoHonorarioCumprimento,
              valorEdicaoProcessoCumprimento,
              setValorEdicaoProcessoCumprimento,
              iniciarEdicaoHonorarioCumprimento,
              salvarEdicaoHonorarioCumprimento,
              cancelarEdicaoHonorarioCumprimento
            )}

            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total de Processos:</small> {formData.honorarios.cumprimento.length}
              </h5>
            </div>
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  const renderStep13 = () => {
    return (
      <Fragment>
        {/* Sub-etapa 1: Requisitórios */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-file-earmark-arrow-up me-2"></i>
              Requisitórios
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={10}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 000000.000.000"
                      value={novoProcessoRequisitorio}
                      onChange={(e) => setNovoProcessoRequisitorio(apenasNumeros(e.target.value))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarOficioRequisitorio();
                        }
                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px 0 0 8px' }}
                    />
                    <Button
                      variant="primary"
                      onClick={adicionarOficioRequisitorio}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Adicionar
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, oficiosRequisitorios: Math.max(1, prev.oficiosRequisitorios - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.oficiosRequisitorios}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, oficiosRequisitorios: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, oficiosRequisitorios: Math.min(50, prev.oficiosRequisitorios + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasOficiosRequisitorios}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.oficiosRequisitorios} linha{quantidadeLinhas.oficiosRequisitorios > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            {renderTabelaProcessosSimples(
              formData.oficiosExpedidos.requisitorios,
              removerOficioRequisitorio,
              editandoOficioRequisitorio,
              valorEdicaoProcessoRequisitorio,
              setValorEdicaoProcessoRequisitorio,
              iniciarEdicaoOficioRequisitorio,
              salvarEdicaoOficioRequisitorio,
              cancelarEdicaoOficioRequisitorio
            )}

            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total de Processos:</small> {formData.oficiosExpedidos.requisitorios.length}
              </h5>
            </div>
          </Card.Body>
        </Card>

        {/* Sub-etapa 2: Demais Ofícios */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0">
              <i className="bi bi-file-earmark-text me-2"></i>
              Demais Ofícios
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={10}>
                <Form.Group>
                  <Form.Label className="fw-bold">N.º do Processo</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 000000.000.000"
                      value={novoProcessoDemaisOficios}
                      onChange={(e) => setNovoProcessoDemaisOficios(apenasNumeros(e.target.value))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarOficioDemais();
                        }
                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault();
                        }
                      }}
                      style={{ borderRadius: '8px 0 0 8px' }}
                    />
                    <Button
                      variant="primary"
                      onClick={adicionarOficioDemais}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Adicionar
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Adicionar</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, oficiosDemais: Math.max(1, prev.oficiosDemais - 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-dash">-</i>
                  </Button>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={quantidadeLinhas.oficiosDemais}
                    onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, oficiosDemais: Math.max(1, parseInt(e.target.value) || 1) }))}
                    style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                    size="sm"
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQuantidadeLinhas(prev => ({ ...prev, oficiosDemais: Math.min(50, prev.oficiosDemais + 1) }))}
                    style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus">+</i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={adicionarMultiplasLinhasOficiosDemais}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Adicionar {quantidadeLinhas.oficiosDemais} linha{quantidadeLinhas.oficiosDemais > 1 ? 's' : ''}
                  </Button>
                </div>
              </Col>
            </Row>

            {renderTabelaProcessosSimples(
              formData.oficiosExpedidos.demais,
              removerOficioDemais,
              editandoOficioDemais,
              valorEdicaoProcessoDemaisOficios,
              setValorEdicaoProcessoDemaisOficios,
              iniciarEdicaoOficioDemais,
              salvarEdicaoOficioDemais,
              cancelarEdicaoOficioDemais
            )}

            <div className="mt-2 text-end">
              <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <small className="text-muted">Total de Processos:</small> {formData.oficiosExpedidos.demais.length}
              </h5>
            </div>
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  const renderStep14 = () => (
    <Fragment>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold">Tipo de Peça</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: Petição Inicial"
              value={novoTipoPeca}
              onChange={(e) => setNovoTipoPeca(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 000000.000.000"
              value={novoNumeroProcessoDesig}
              onChange={(e) => setNovoNumeroProcessoDesig(apenasNumeros(e.target.value))}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="fw-bold">Defensoria do Processo/Vara</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 1ª Vara Cível"
              value={novaDefensoriaVara}
              onChange={(e) => setNovaDefensoriaVara(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={1} className="d-flex align-items-end">
          <Button
            variant="primary"
            onClick={adicionarDesignacaoEspecialProcesso}
            style={{ borderRadius: '8px' }}
            className="w-100"
          >
            <i className="bi bi-plus-lg me-2"></i>
            Adicionar
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Adicionar</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialProcessos: Math.max(1, prev.designacaoEspecialProcessos - 1) }))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-dash">-</i>
            </Button>
            <Form.Control
              type="number"
              min="1"
              max="50"
              value={quantidadeLinhas.designacaoEspecialProcessos}
              onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialProcessos: Math.max(1, parseInt(e.target.value) || 1) }))}
              style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
              size="sm"
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialProcessos: Math.min(50, prev.designacaoEspecialProcessos + 1) }))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-plus">+</i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={adicionarMultiplasLinhasDesignacaoEspecialProcessos}
              style={{ borderRadius: '8px' }}
            >
              <i className="bi bi-plus-lg me-1"></i>
              Adicionar {quantidadeLinhas.designacaoEspecialProcessos} linha{quantidadeLinhas.designacaoEspecialProcessos > 1 ? 's' : ''}
            </Button>
          </div>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end">
          <h5 className="mb-0 text-primary">
            <small className="text-muted">Total de Itens:</small> {formData.designacaoEspecialProcessos.itens.length}
          </h5>
        </Col>
      </Row>

      <Table bordered hover>
        <thead className="table-light">
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '25%' }}>Tipo de Peça</th>
            <th style={{ width: '25%' }}>N.º do Processo</th>
            <th style={{ width: '25%' }}>Defensoria do Processo/Vara</th>
            <th style={{ width: '20%' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {formData.designacaoEspecialProcessos.itens.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                Nenhum item adicionado ainda
              </td>
            </tr>
          ) : (
            formData.designacaoEspecialProcessos.itens.map((item, index) => {
              const estaEditando = editandoDesignacaoEspecialProcesso === item.id;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: Petição Inicial"
                        value={valorEdicaoTipoPeca}
                        onChange={(e) => setValorEdicaoTipoPeca(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.tipoPeca
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: 000000.000.000"
                        value={valorEdicaoNumeroProcessoDesig}
                        onChange={(e) => setValorEdicaoNumeroProcessoDesig(apenasNumeros(e.target.value))}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.numeroProcesso
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: 1ª Vara Cível"
                        value={valorEdicaoDefensoriaVara}
                        onChange={(e) => setValorEdicaoDefensoriaVara(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.defensoriaVara
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="success"
                          size="sm"
                          onClick={() => salvarEdicaoDesignacaoEspecialProcesso(item.id)}
                          title="Salvar"
                        >
                          <i className="bi bi-check-lg"> Salvar</i>
                        </Button>
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="secondary"
                          size="sm"
                          onClick={cancelarEdicaoDesignacaoEspecialProcesso}
                          title="Cancelar"
                        >
                          <i className="bi bi-x-lg"> Cancelar</i>
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="outline-primary"
                          size="sm"
                          onClick={() => iniciarEdicaoDesignacaoEspecialProcesso(item)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"> Editar</i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removerDesignacaoEspecialProcesso(item.id)}
                          style={{ width: '80px', height: '40px' }}
                          title="Excluir"
                        >
                          <i className="bi bi-trash"> Excluir</i>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </Fragment>
  );

  const renderStep15 = () => (
    <Fragment>
      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="fw-bold">Tipo de peça</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: Audiência de Instrução"
              value={novaAudienciaDesig}
              onChange={(e) => setNovaAudienciaDesig(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 000000.000.000"
              value={novoNumeroProcessoDesigAud}
              onChange={(e) => setNovoNumeroProcessoDesigAud(apenasNumeros(e.target.value))}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label className="fw-bold">Data</Form.Label>
            <Form.Control
              type="date"
              value={novaDataDesigAud}
              onChange={(e) => setNovaDataDesigAud(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="fw-bold">Defensoria do Processo/Vara</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 1ª Vara Cível"
              value={novaDefensoriaVaraDesigAud}
              onChange={(e) => setNovaDefensoriaVaraDesigAud(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={1} className="d-flex align-items-end">
          <Button
            variant="primary"
            onClick={adicionarDesignacaoEspecialAudiencia}
            style={{ borderRadius: '8px' }}
            className="w-100"
            disabled={isViewMode}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Adicionar
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Adicionar</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialAudiencias: Math.max(1, prev.designacaoEspecialAudiencias - 1) }))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-dash">-</i>
            </Button>
            <Form.Control
              type="number"
              min="1"
              max="50"
              value={quantidadeLinhas.designacaoEspecialAudiencias}
              onChange={(e) => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialAudiencias: Math.max(1, parseInt(e.target.value) || 1) }))}
              style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
              size="sm"
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhas(prev => ({ ...prev, designacaoEspecialAudiencias: Math.min(50, prev.designacaoEspecialAudiencias + 1) }))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-plus">+</i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={adicionarMultiplasLinhasDesignacaoEspecialAudiencias}
              style={{ borderRadius: '8px' }}
            >
              <i className="bi bi-plus-lg me-1"></i>
              Adicionar {quantidadeLinhas.designacaoEspecialAudiencias} linha{quantidadeLinhas.designacaoEspecialAudiencias > 1 ? 's' : ''}
            </Button>
          </div>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end">
          <h5 className="mb-0 text-primary">
            <small className="text-muted">Total de Itens:</small> {formData.designacaoEspecialAudiencias.itens.length}
          </h5>
        </Col>
      </Row>

      <Table bordered hover>
        <thead className="table-light">
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '20%' }}>Audiência</th>
            <th style={{ width: '20%' }}>N.º do Processo</th>
            <th style={{ width: '15%' }}>Data</th>
            <th style={{ width: '20%' }}>Defensoria do Processo/Vara</th>
            <th style={{ width: '20%' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {formData.designacaoEspecialAudiencias.itens.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                Nenhum item adicionado ainda
              </td>
            </tr>
          ) : (
            formData.designacaoEspecialAudiencias.itens.map((item, index) => {
              const estaEditando = editandoDesignacaoEspecialAudiencia === item.id;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: Audiência de Instrução"
                        value={valorEdicaoAudienciaDesig}
                        onChange={(e) => setValorEdicaoAudienciaDesig(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.audiencia
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: 000000.000.000"
                        value={valorEdicaoNumeroProcessoDesigAud}
                        onChange={(e) => setValorEdicaoNumeroProcessoDesigAud(apenasNumeros(e.target.value))}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.numeroProcesso
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="date"
                        size="sm"
                        value={valorEdicaoDataDesigAud}
                        onChange={(e) => setValorEdicaoDataDesigAud(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      formatarDataHelper(item.data)
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: 1ª Vara Cível"
                        value={valorEdicaoDefensoriaVaraDesigAud}
                        onChange={(e) => setValorEdicaoDefensoriaVaraDesigAud(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.defensoriaVara
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="success"
                          size="sm"
                          onClick={() => salvarEdicaoDesignacaoEspecialAudiencia(item.id)}
                          title="Salvar"
                        >
                          <i className="bi bi-check-lg"> Salvar</i>
                        </Button>
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="secondary"
                          size="sm"
                          onClick={cancelarEdicaoDesignacaoEspecialAudiencia}
                          title="Cancelar"
                        >
                          <i className="bi bi-x-lg"> Cancelar</i>
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <Button
                          style={{ width: '80px', height: '40px' }}
                          variant="outline-primary"
                          size="sm"
                          onClick={() => iniciarEdicaoDesignacaoEspecialAudiencia(item)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"> Editar</i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removerDesignacaoEspecialAudiencia(item.id)}
                          style={{ width: '80px', height: '40px' }}
                          title="Excluir"
                        >
                          <i className="bi bi-trash"> Excluir</i>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </Fragment>
  );

  const renderStep16 = () => (
    <Fragment>
      <Row className="mb-4">
        <Col md={12}>
          <Form.Group>
            <Form.Label className="fw-bold">N.º do Processo</Form.Label>
            <ProcessoInput
              value={novoProcessoOutros}
              onChange={setNovoProcessoOutros}
              onAdicionar={() => adicionarProcesso('outrosProcedimentos', novoProcessoOutros, setNovoProcessoOutros)}
            />
            <Form.Text className="text-muted">Pressione Enter ou clique em Adicionar para incluir o processo na lista</Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <ControlesLinha
        quantidade={quantidadeLinhas.outrosProcedimentos}
        onDecrementar={() => setQuantidadeLinhas(prev => ({ ...prev, outrosProcedimentos: Math.max(1, prev.outrosProcedimentos - 1) }))}
        onIncrementar={() => setQuantidadeLinhas(prev => ({ ...prev, outrosProcedimentos: Math.min(50, prev.outrosProcedimentos + 1) }))}
        onQuantidadeChange={(value) => setQuantidadeLinhas(prev => ({ ...prev, outrosProcedimentos: value }))}
        onAdicionar={() => adicionarMultiplasLinhas('outrosProcedimentos')}
        totalLabel="Total de Processos"
        totalCount={formData.outrosProcedimentos.processos.length}
      />
      {renderTabelaProcessos('outrosProcedimentos', formData.outrosProcedimentos.processos)}
    </Fragment>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      case 9: return renderStep9();
      case 10: return renderStep10();
      case 11: return renderStep11();
      case 12: return renderStep12();
      case 13: return renderStep13();
      case 14: return renderStep14();
      case 15: return renderStep15();
      case 16: return renderStep16();
      default: return renderStep1();
    }
  };

  if (loading) {
    return <Loading />;
  }

  const titularidadeInfo = getTitularidadeLabel(titularidade);
  const progressPercent = (currentStep / WIZARD_STEPS.length) * 100;

  return (
    <Fragment>
      <style>{`
        @keyframes triBlink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.25; transform: scale(1.06); }
        }
        .blink-tri {
          animation: triBlink 1s infinite ease-in-out;
        }
      `}</style>
      <Container fluid>
        <div className="mb-4">
          <div className="d-flex align-items-end justify-content-between gap-2">
            <h2 className="mb-0">
              {isViewMode ? 'Visualizar Relatório de Atividades - Cível' : 'Novo Relatório de Atividades - Cível'}
            </h2>
            <div className="d-flex align-items-center gap-2">
              <RelatoriosEscolhaModuloButton />
              <RelatoriosUserLabel className="mb-1" />
            </div>
          </div>
          <p className="text-muted">
            {isViewMode
              ? 'Visualizando os dados do relatório de atividades cível (modo somente leitura)'
              : 'Preencha os dados do relatório de atividades cível para o período selecionado'
            }
          </p>
        </div>

        {isViewMode && origemVisualizacao !== 'corregedoria' && (
          <div className="alert alert-info mb-4" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Modo Visualização:</strong> Você está visualizando este relatório em modo somente leitura.
            Para editar, clique no botão "Editar" na lista de relatórios.
          </div>
        )}

        {/* Card de Informações do Relatório */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>Informações do Relatório</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <h6 className="text-muted">Defensor</h6>
                <p className="mb-2">
                  <strong>{defensorData?.pessoa?.nome || 'Não informado'}</strong>
                </p>
              </Col>
              <Col md={2}>
                <h6 className="text-muted">Titularidade</h6>
                <p className="mb-2">
                  <Badge bg={titularidadeInfo.variant}>{titularidadeInfo.label}</Badge>
                </p>
              </Col>
              <Col md={2}>
                <h6 className="text-muted">Período</h6>
                <p className="mb-2">
                  <strong>{getMesLabel(formData.mes)}/{formData.ano}</strong>
                </p>
              </Col>
              <Col md={3}>
                <h6 className="text-muted">Defensoria/Vara</h6>
                <p className="mb-2">
                  <strong>{formData.defensoriaName || vara}</strong>
                </p>
              </Col>
              <Col md={2}>
                <h6 className="text-muted">Tipo</h6>
                <p className="mb-2">
                  <Badge bg="primary" style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>Cível</Badge>
                </p>
              </Col>
            </Row>

          </Card.Body>
        </Card>

        {/* Wizard Steps Navigation */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Body>
            <ProgressBar
              now={progressPercent}
              label={`${currentStep} de ${WIZARD_STEPS.length}`}
              className="mb-3"
              style={{ height: '25px', backgroundColor: RELATORIO_CORES.TERCIARIA }}
            >
              <div style={{
                width: `${progressPercent}%`,
                backgroundColor: RELATORIO_CORES.PRIMARIA,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 'bold'
              }}>
                {currentStep} de {WIZARD_STEPS.length}
              </div>
            </ProgressBar>
            <Nav variant="pills" className="flex-row justify-content-between">
              {WIZARD_STEPS.map((step) => (
                <Nav.Item key={step.id}>
                  <Nav.Link
                    active={currentStep === step.id}
                    onClick={() => handleGoToStep(step.id)}
                    className={`
                      ${currentStep === step.id ? '' : ''}
                      ${currentStep > step.id ? '' : ''}
                      cursor-pointer
                    `}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: currentStep === step.id ? RELATORIO_CORES.PRIMARIA : 'transparent',
                      color: currentStep === step.id ? '#fff' : (currentStep > step.id ? RELATORIO_CORES.PRIMARIA : '#000')
                    }}
                  >
                    <span className="d-none d-lg-inline">{step.id}. {step.title}</span>
                    <span className="d-lg-none">{step.id}. {step.shortTitle}</span>
                    {currentStep > step.id && <i className="bi bi-check-circle-fill ms-2" style={{ color: RELATORIO_CORES.PRIMARIA }}></i>}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Body>
        </Card>

        {/* Card do Formulário */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {WIZARD_STEPS[currentStep - 1].title}
              </h5>
              <Badge bg="secondary">
                {currentStep} / {WIZARD_STEPS.length}
              </Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <Form>
              <fieldset disabled={isViewMode} style={{ margin: 0, padding: 0, border: 0 }}>
                {renderCurrentStep()}
              </fieldset>
            </Form>
          </Card.Body>
        </Card>

        {/* Botões de Navegação */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Body>
            {/* Resumo do Relatório - Quantidade de itens por seção */}
            <div className="mb-2 d-inline-flex align-items-center flex-nowrap gap-2">
              <h6 className="mb-0 fw-bold d-inline-flex align-items-center" style={{ color: RELATORIO_CORES.PRIMARIA }}>
                <i className="bi bi-bar-chart-steps me-2"></i>
                <span>Resumo do Relatório</span>
              </h6>
              <Button
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center"
                onClick={() => setMostrarResumoRelatorio(prev => !prev)}
                title={mostrarResumoRelatorio ? 'Ocultar resumo' : 'Mostrar resumo'}
                aria-label={mostrarResumoRelatorio ? 'Ocultar resumo' : 'Mostrar resumo'}
                style={{
                  borderRadius: '8px',
                  lineHeight: 1,
                }}
              >
                {mostrarResumoRelatorio ? 'Ocultar Resumo' : 'Mostrar Resumo'}
              </Button>
            </div>
            {mostrarResumoRelatorio && (
              <div className="border rounded mb-3">
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60%' }}>Seção / Subseção</th>
                      <th style={{ width: '40%', textAlign: 'center' }}>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>2. Autor e Natureza da Ação</td><td className="text-center">{formData.autorNatureza.itens.length}</td></tr>
                    <tr><td>3. Contestação</td><td className="text-center">{formData.contestacao.processos.length}</td></tr>
                    <tr><td>4. Reconvenção</td><td className="text-center">{formData.reconvencao.processos.length}</td></tr>
                    <tr><td>5. Réplica</td><td className="text-center">{formData.replica.processos.length}</td></tr>
                    <tr><td>6. Manifestação nos Autos</td><td className="text-center">{formData.manifestacao.processos.length}</td></tr>
                    <tr><td>7. Razões Finais</td><td className="text-center">{formData.razoesFinais.processos.length}</td></tr>
                    <tr><td>8. Audiências</td><td className="text-center">—</td></tr>
                    <tr><td className="ps-4"><small>Audiências Presenciais</small></td><td className="text-center">{formData.audiencias.presenciais.length}</td></tr>
                    <tr><td className="ps-4"><small>Audiências Remotas</small></td><td className="text-center">{formData.audiencias.remotas.length}</td></tr>
                    <tr><td>9. Recursos</td><td className="text-center">{formData.recursos.processos.length}</td></tr>
                    <tr><td>10. Atendimentos</td><td className="text-center">—</td></tr>
                    <tr><td className="ps-4"><small>Atendimentos Presenciais</small></td><td className="text-center">{formData.atendimento.presenciais.length}</td></tr>
                    <tr><td className="ps-4"><small>Atendimentos Remotos</small></td><td className="text-center">{formData.atendimento.remotas.length}</td></tr>
                    <tr><td>11. Acordos Extrajudiciais</td><td className="text-center">{formData.acordosExtrajudiciais.itens.length}</td></tr>
                    <tr><td>12. Honorários</td><td className="text-center">—</td></tr>
                    <tr><td className="ps-4"><small>Sentença/Acórdão</small></td><td className="text-center">{formData.honorarios.sentencaAcordao.length}</td></tr>
                    <tr><td className="ps-4"><small>Cumprimento</small></td><td className="text-center">{formData.honorarios.cumprimento.length}</td></tr>
                    <tr><td>13. Ofícios Expedidos</td><td className="text-center">—</td></tr>
                    <tr><td className="ps-4"><small>Requisitórios</small></td><td className="text-center">{formData.oficiosExpedidos.requisitorios.length}</td></tr>
                    <tr><td className="ps-4"><small>Demais</small></td><td className="text-center">{formData.oficiosExpedidos.demais.length}</td></tr>
                    <tr><td>14. Designação Especial (Processos)</td><td className="text-center">{formData.designacaoEspecialProcessos.itens.length}</td></tr>
                    <tr><td>15. Designação Especial (Audiências)</td><td className="text-center">{formData.designacaoEspecialAudiencias.itens.length}</td></tr>
                    <tr><td>16. Outros Procedimentos</td><td className="text-center">{formData.outrosProcedimentos.processos.length}</td></tr>
                    <tr className="table-light">
                      <td className="fw-bold">Total de itens</td>
                      <td className="text-center fw-bold">
                        {
                          formData.autorNatureza.itens.length +
                          formData.contestacao.processos.length +
                          formData.reconvencao.processos.length +
                          formData.replica.processos.length +
                          formData.manifestacao.processos.length +
                          formData.razoesFinais.processos.length +
                          formData.audiencias.presenciais.length +
                          formData.audiencias.remotas.length +
                          formData.recursos.processos.length +
                          formData.atendimento.presenciais.length +
                          formData.atendimento.remotas.length +
                          formData.acordosExtrajudiciais.itens.length +
                          formData.honorarios.sentencaAcordao.length +
                          formData.honorarios.cumprimento.length +
                          formData.oficiosExpedidos.requisitorios.length +
                          formData.oficiosExpedidos.demais.length +
                          formData.designacaoEspecialProcessos.itens.length +
                          formData.designacaoEspecialAudiencias.itens.length +
                          formData.outrosProcedimentos.processos.length
                        }
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
            <div className="d-flex justify-content-between">
              <div>
                <Button
                  variant="outline-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                  className="me-2"
                >
                  Cancelar
                </Button>
                {currentStep > 1 && (
                  <Button
                    variant="outline-primary"
                    onClick={handlePrevStep}
                    disabled={saving}
                    style={{ borderColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.PRIMARIA }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Anterior
                  </Button>
                )}
              </div>
              <div className="d-flex gap-2">
                {isViewMode ? (
                  <>
                    {currentStep < WIZARD_STEPS.length && (
                      <Button
                        variant="primary"
                        onClick={handleNextStep}
                        disabled={saving}
                        style={{ backgroundColor: RELATORIO_CORES.PRIMARIA, borderColor: RELATORIO_CORES.PRIMARIA }}
                      >
                        Próximo
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => navigate(Links.Relatorios)}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Voltar para Lista
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSave(true)}
                      disabled={saving}
                      style={{ borderColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.PRIMARIA }}
                    >
                      {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                    {currentStep < WIZARD_STEPS.length ? (
                      <Button
                        variant="primary"
                        onClick={handleNextStep}
                        disabled={saving}
                        style={{ backgroundColor: RELATORIO_CORES.PRIMARIA, borderColor: RELATORIO_CORES.PRIMARIA }}
                      >
                        Próximo
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        onClick={() => handleSave(false)}
                        disabled={saving || !mesRelatorioTerminado()}
                        title={!mesRelatorioTerminado() ? 'O relatório ainda não pode ser enviado devido ao prazo.' : ''}
                        style={{ backgroundColor: RELATORIO_CORES.PRIMARIA, borderColor: RELATORIO_CORES.PRIMARIA }}
                      >
                        {saving
                          ? 'Enviando...'
                          : metaReenvioCorregedoria
                            ? 'Enviar à Corregedoria'
                            : 'Enviar Relatório'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </Fragment>
  );
}
